import React, { useState, useRef, useEffect } from "react";

// ── API BASE URL ─────────────────────────────────────
// In production: points to your Railway backend
// In development: points to localhost
const API_BASE = process.env.REACT_APP_API_URL || "";


/* ─────────────────────────────────────────────
   ARCANA DATA  (1-22 system)
───────────────────────────────────────────── */
const ARCANA = {
  1:  { name:"The Magician",       roman:"I",     theme:"Will & Manifestation",    color:"#FFD97A", traits:["Creative power","Resourcefulness","Initiative","Confidence"], shadow:["Manipulation","Overconfidence","Scattered energy"], desc:"Master of transformation — turns thought into reality through focused will." },
  2:  { name:"High Priestess",     roman:"II",    theme:"Intuition & Mystery",     color:"#C0B8FF", traits:["Deep intuition","Inner wisdom","Patience","Mysticism"], shadow:["Secrecy","Withdrawal","Emotional detachment"], desc:"Guardian of hidden knowledge, one who perceives beyond the veil." },
  3:  { name:"The Empress",        roman:"III",   theme:"Creativity & Abundance",  color:"#90EE90", traits:["Nurturing","Creativity","Abundance","Beauty"], shadow:["Over-giving","Codependency","Creative blocks"], desc:"Divine mother — source of creative power, beauty, and natural abundance." },
  4:  { name:"The Emperor",        roman:"IV",    theme:"Structure & Authority",   color:"#FF8C69", traits:["Leadership","Stability","Discipline","Authority"], shadow:["Rigidity","Control issues","Domination"], desc:"Architect of reality — creates order and provides steadfast, grounded leadership." },
  5:  { name:"The Hierophant",     roman:"V",     theme:"Wisdom & Sacred Teaching",color:"#DDA0DD", traits:["Wisdom","Spiritual guidance","Tradition","Teaching"], shadow:["Dogmatism","Blind conformity","Rigid beliefs"], desc:"Bridge between heaven and earth, passing sacred wisdom to those who seek." },
  6:  { name:"The Lovers",         roman:"VI",    theme:"Love & Sacred Choice",    color:"#FF9BB0", traits:["Deep love","Harmony","Aligned values","Partnership"], shadow:["Indecision","Disharmony","Relationship conflict"], desc:"Sacred union of opposites — the profound dance of love and conscious choice." },
  7:  { name:"The Chariot",        roman:"VII",   theme:"Victory & Mastery",       color:"#87CEFA", traits:["Determination","Victory","Willpower","Focus"], shadow:["Aggression","Overdriving","Lack of direction"], desc:"Victorious warrior who masters opposing forces through sheer determination." },
  8:  { name:"Strength",           roman:"VIII",  theme:"Inner Power & Grace",     color:"#FFA07A", traits:["Courage","Patience","Inner strength","Compassion"], shadow:["Self-doubt","Repression","Weakness"], desc:"Gentle power that tames the wild — strength flowing from love, not force." },
  9:  { name:"The Hermit",         roman:"IX",    theme:"Wisdom & Illumination",   color:"#F0E68C", traits:["Deep wisdom","Introspection","Inner guidance","Illumination"], shadow:["Isolation","Over-withdrawal","Loneliness"], desc:"Lantern-bearer on the mountain — illuminating the path for self and others." },
  10: { name:"Wheel of Fortune",   roman:"X",     theme:"Destiny & Sacred Cycles", color:"#20B2AA", traits:["Destiny alignment","Divine flow","Cycles","Synchronicity"], shadow:["Resistance to change","Victim mentality","Missed cycles"], desc:"The great wheel turns — bringing cosmic opportunities and karmic completion." },
  11: { name:"Justice",            roman:"XI",    theme:"Balance & Sacred Truth",  color:"#B8B02A", traits:["Fairness","Truth","Integrity","Cause & effect"], shadow:["Harsh judgment","Rigidity","Moral absolutism"], desc:"Perfect cosmic balance — navigating life with integrity and clear truth." },
  12: { name:"The Hanged Man",     roman:"XII",   theme:"Surrender & Insight",     color:"#9F7FD0", traits:["New perspectives","Surrender","Spiritual insight","Patience"], shadow:["Stagnation","Martyrdom","Resistance"], desc:"One who gains profound wisdom through stillness, surrender, and release." },
  13: { name:"Death",              roman:"XIII",  theme:"Transformation & Rebirth",color:"#A0A0A8", traits:["Transformation","Release","Renewal","Powerful change"], shadow:["Fear of change","Resistance","Stagnation"], desc:"The great transformer — clearing the old to make space for magnificent rebirth." },
  14: { name:"Temperance",         roman:"XIV",   theme:"Balance & Higher Purpose",color:"#7FFFD4", traits:["Balance","Moderation","Higher purpose","Alchemy"], shadow:["Excess","Imbalance","Lack of vision"], desc:"Alchemist of life — blending opposing energies into something transcendent." },
  15: { name:"The Devil",          roman:"XV",    theme:"Shadow & Liberation",     color:"#CD5C5C", traits:["Shadow integration","Material mastery","Breakthrough","Liberation"], shadow:["Addiction","Bondage","Materialism"], desc:"Mirror of the shadow — offering liberation through confronting inner darkness." },
  16: { name:"The Tower",          roman:"XVI",   theme:"Revelation & Breakthrough",color:"#FF6347", traits:["Breakthrough","Revelation","Necessary change","Awakening"], shadow:["Chaos","Resistance to truth","Upheaval"], desc:"The great revealer — shattering illusions to reveal the eternal truth beneath." },
  17: { name:"The Star",           roman:"XVII",  theme:"Hope & Authentic Renewal",color:"#ADD8E6", traits:["Hope","Inspiration","Healing","Authentic expression"], shadow:["Disconnection","Lost faith","Wishful thinking"], desc:"Beacon of hope after darkness — shining authentic light into the world." },
  18: { name:"The Moon",           roman:"XVIII", theme:"Intuition & Sacred Dreams",color:"#E6E6FA", traits:["Deep intuition","Psychic gifts","Creative dreams","Mystery"], shadow:["Illusion","Fear","Confusion"], desc:"Illuminator of the unconscious — navigating mystery through refined intuition." },
  19: { name:"The Sun",            roman:"XIX",   theme:"Joy & Radiant Vitality",  color:"#FFD700", traits:["Radiant joy","Success","Vitality","Childlike wonder"], shadow:["Ego inflation","Burnout","Naivety"], desc:"Great light-bringer — radiating authentic joy and inspiring all who draw near." },
  20: { name:"Judgement",          roman:"XX",    theme:"Awakening & Divine Calling",color:"#FFA500", traits:["Awakening","Divine calling","Absolution","Rebirth"], shadow:["Self-judgment","Ignoring the call","Stagnation"], desc:"Herald of awakening — one who answers the call of their highest potential." },
  21: { name:"The World",          roman:"XXI",   theme:"Wholeness & Completion",  color:"#7CFC00", traits:["Completion","Integration","Mastery","Wholeness"], shadow:["Incompletion","Lack of closure","Refusing journey"], desc:"Dancing soul of completion — having integrated all of life's sacred lessons." },
  22: { name:"The Fool",           roman:"XXII",  theme:"New Beginnings & Freedom",color:"#98FB98", traits:["Freedom","New beginnings","Trust in the universe","Pure potential"], shadow:["Recklessness","Naivety","Avoidance"], desc:"Sacred innocent leaping into the unknown — pure potential trusting the journey." },
};

/* ─────────────────────────────────────────────
   CALCULATION ENGINE
───────────────────────────────────────────── */
const digitSum = n => String(Math.abs(n)).split("").reduce((s,d) => s + parseInt(d), 0);
const toArcana = n => { while (n > 22) n = digitSum(n); return n || 22; };

function calcMatrix(day, month, year) {
  const sky   = toArcana(day);
  const earth = toArcana(month);
  const fire  = toArcana(digitSum(year));
  const water = toArcana(sky + earth);
  const topRight    = toArcana(sky + fire);
  const bottomRight = toArcana(earth + fire);
  const bottomLeft  = toArcana(earth + water);
  const topLeft     = toArcana(sky + water);
  const center      = toArcana(sky + earth + fire + water);
  const karma       = toArcana(water + fire);
  const social      = toArcana(center + earth);
  const comfort     = toArcana(center + sky);
  return { sky, earth, fire, water, topRight, bottomRight, bottomLeft, topLeft, center, karma, social, comfort };
}

/* ─────────────────────────────────────────────
   AI INTERPRETATION (calls Anthropic API)
───────────────────────────────────────────── */
async function getInterpretation(mat, fd) {
  const nums = Object.entries(mat)
    .map(([k,v]) => `${k}=${v}(${ARCANA[v]?.name})`)
    .join(", ");
  const prompt = `You are a master Destiny Matrix reader with deep knowledge of the 22-arcana system. 
Generate a warm, insightful, personalized matrix reading for someone born on ${fd.day}/${fd.month}/${fd.year}${fd.name ? ` named ${fd.name}` : ""}.

Matrix positions: ${nums}
Core Essence (center): ${mat.center} — ${ARCANA[mat.center]?.name}
Soul (sky): ${mat.sky} — ${ARCANA[mat.sky]?.name}
Purpose (earth): ${mat.earth} — ${ARCANA[mat.earth]?.name}
Destiny (fire): ${mat.fire} — ${ARCANA[mat.fire]?.name}
Love (topRight): ${mat.topRight} — ${ARCANA[mat.topRight]?.name}

CRITICAL WRITING RULES — you MUST follow every rule for every single sentence in every field:
- Every sentence must begin with a clear subject (a noun or pronoun) followed by a verb. No exceptions.
- FORBIDDEN sentence starters — never open a sentence with: "And", "But", "Because", "With", "Through", "As", "Having", "Being", "Drawing", "By", "Into", "From", "For", "This energy", "Such", or any participial/prepositional phrase fragment.
- Every sentence must be grammatically complete and self-contained — never a fragment, never a clause missing a subject.
- Write as polished, published prose: each sentence must make perfect sense when read in isolation.

Respond ONLY with valid JSON, no markdown fences, no preamble:
{
  "summary": "3-4 complete sentences giving a poetic soul blueprint overview (60-80 words, addressed to 'you')",
  "personality": "3-4 complete sentences giving a deep personality analysis referencing specific arcana energies (60-80 words)",
  "love": "3-4 complete sentences giving a love & relationship pattern analysis (60-80 words)",
  "career": "3-4 complete sentences analysing professional destiny, career archetype, and how the fire and abundance arcana shape the person's work path and financial flow (60-80 words)",
  "business": "3-4 complete sentences describing entrepreneurial gifts, leadership authority, and business mission based on the earth and core essence arcana — what kind of business or impact this person is built to create (60-80 words)",
  "yearPrediction": "2-3 complete sentences previewing this year's energy based on the matrix (40-55 words)",
  "strengths": ["5 specific strength traits — each a short noun phrase, not a full sentence"],
  "challenges": ["3 specific growth areas or shadow patterns — each a short noun phrase, not a full sentence"],
  "affirmation": "one powerful complete-sentence personal affirmation beginning with 'I' (max 20 words)"
}`;

  try {
    const res = await fetch(`${API_BASE}/api/interpret`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "API error");
    const text = data.content?.find(b => b.type === "text")?.text || "";
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch {
    return {
      summary: `Your matrix reveals a profound soul carrying the energy of ${ARCANA[mat.center]?.name} at your core — a being of ${ARCANA[mat.center]?.theme.toLowerCase()}. Your soul number ${mat.sky} gifts you with ${ARCANA[mat.sky]?.traits[0]?.toLowerCase()} and ${ARCANA[mat.sky]?.traits[1]?.toLowerCase()}, while your destiny number ${mat.fire} calls you toward ${ARCANA[mat.fire]?.theme.toLowerCase()}.`,
      personality: `As ${ARCANA[mat.center]?.name} at your core, you embody ${ARCANA[mat.center]?.desc} Your soul vibration carries the gifts of ${(ARCANA[mat.sky]?.traits || []).slice(0,2).join(" and ")}, which shape how you move through the world and engage with your deepest truth.`,
      love: `Your love number ${mat.topRight} — ${ARCANA[mat.topRight]?.name} — reveals that ${ARCANA[mat.topRight]?.desc} In relationships you bring ${(ARCANA[mat.topRight]?.traits || []).slice(0,2).join(" and ")}, seeking partners who honor the depth of your soul's journey.`,
      career: `Your destiny number ${mat.fire} — ${ARCANA[mat.fire]?.name} — points toward ${ARCANA[mat.fire]?.theme.toLowerCase()} as your professional calling. Your abundance position ${mat.bottomRight} (${ARCANA[mat.bottomRight]?.name}) shapes how material success flows through your work. Your most aligned career channels ${(ARCANA[mat.fire]?.traits || []).slice(0,2).join(" and ")} in service of something larger than yourself.`,
      business: `Your life purpose number ${mat.earth} — ${ARCANA[mat.earth]?.name} — defines the mission your business is here to fulfil. Your core essence ${mat.center} (${ARCANA[mat.center]?.name}) gives you the leadership presence and personal authority that naturally draws clients and opportunities to you. Your most powerful entrepreneurial path fuses ${ARCANA[mat.earth]?.theme.toLowerCase()} with the visionary magnetism of ${ARCANA[mat.center]?.name}.`,
      yearPrediction: `This year your ${ARCANA[mat.fire]?.name} destiny energy activates powerfully. Themes of ${ARCANA[mat.fire]?.theme.toLowerCase()} run through your most important decisions. Trust the cosmic timing — your matrix is aligned for meaningful progress.`,
      strengths: ARCANA[mat.center]?.traits || ["Intuition","Wisdom","Creativity","Resilience","Depth"],
      challenges: ARCANA[mat.center]?.shadow || ["Shadow integration","Fear of depth","Resistance to flow"],
      affirmation: `I embody the power of ${ARCANA[mat.center]?.name} and trust my sacred path.`,
    };
  }
}

/* ─────────────────────────────────────────────
   COMPATIBILITY UTILS
───────────────────────────────────────────── */
function calcCompatibilityScore(mat1, mat2) {
  const weights = { center:3, sky:2, earth:2, topRight:2, fire:2, water:1, topLeft:1, bottomRight:1, bottomLeft:1 };
  let weighted = 0, total = 0;
  for (const [k, w] of Object.entries(weights)) {
    total += w;
    const diff = Math.abs(mat1[k] - mat2[k]);
    weighted += w * Math.max(0, 1 - diff / 11);
  }
  return Math.round((weighted / total) * 100);
}

async function getCompatibilityReading(mat1, mat2, fd1, fd2, score) {
  try {
    const res = await fetch(`${API_BASE}/api/compatibility`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mat1, mat2, fd1, fd2, score }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error("API error");
    const text = data.content?.find(b => b.type === "text")?.text || "";
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch {
    return {
      bond: `${fd1.name || "Person 1"} and ${fd2.name || "Person 2"} share a profound soul connection — the matrix reveals complementary energies that create both harmony and meaningful growth. Their union carries the weight of cosmic design, two distinct forces drawn together by forces beyond ordinary circumstance.`,
      dynamic: `Their daily dynamic blends ${ARCANA[mat1.center]?.name}'s essence with ${ARCANA[mat2.center]?.name}'s energy — a dance of ${ARCANA[mat1.center]?.theme.toLowerCase()} meeting ${ARCANA[mat2.center]?.theme.toLowerCase()}. Moments of deep resonance alternate with creative tension that ultimately strengthens the bond.`,
      strengths: ["Complementary soul energies","Shared karmic purpose","Mutual spiritual growth","Deep intuitive understanding"],
      challenges: ["Balancing different life rhythms","Navigating shadow patterns","Maintaining individual identity"],
      karmic: `Their matrices suggest a multi-lifetime bond — souls who have walked together before and chosen to reunite for a specific sacred purpose. The karmic thread between them is strong and intentional.`,
      advice: `Honor both your individual paths — your union is strongest when each soul shines fully.`,
    };
  }
}

/* ─────────────────────────────────────────────
   SHARE CARD GENERATORS
───────────────────────────────────────────── */
function rRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

async function generateCompatCanvas(mat1, mat2, fd1, fd2, score) {
  await document.fonts.ready;
  const W = 1200, H = 630, SC = 2;
  const canvas = document.createElement("canvas");
  canvas.width = W * SC; canvas.height = H * SC;
  const ctx = canvas.getContext("2d");
  ctx.scale(SC, SC);

  ctx.fillStyle = "#05050E"; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, 380);
  glow.addColorStop(0, "rgba(91,63,166,.22)"); glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(201,164,101,.35)"; ctx.lineWidth = 1;
  ctx.strokeRect(22, 22, W - 44, H - 44);

  ctx.fillStyle = "#C9A465"; ctx.font = '600 11px "Jost",Arial,sans-serif';
  ctx.textAlign = "center";
  ctx.fillText("✦  AI MATRIX DESTINY  ·  LOVE COMPATIBILITY  ✦", W/2, 56);

  const n1 = fd1?.name || "Person 1", n2 = fd2?.name || "Person 2";
  ctx.fillStyle = "#F0EBE1"; ctx.font = '300 56px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(`${n1}  ♡  ${n2}`, W/2, 138);
  ctx.fillStyle = "rgba(240,235,225,.38)"; ctx.font = '400 13px "Jost",Arial,sans-serif';
  ctx.fillText(`${fd1.day}/${fd1.month}/${fd1.year}   ·   ${fd2.day}/${fd2.month}/${fd2.year}`, W/2, 165);

  ctx.strokeStyle = "rgba(201,164,101,.13)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(50, 188); ctx.lineTo(W-50, 188); ctx.stroke();

  const dimScore = k => Math.round(Math.max(0, 1 - Math.abs(mat1[k] - mat2[k]) / 11) * 100);
  const GOLD = "#C9A465";
  const drawDimL = (label, pct, y) => {
    const BW = 195;
    ctx.fillStyle = "rgba(240,235,225,.45)"; ctx.font = '400 10px "Jost",Arial,sans-serif'; ctx.textAlign = "left";
    ctx.fillText(label, 58, y);
    ctx.fillStyle = GOLD; ctx.font = '600 10px "Jost",Arial,sans-serif';
    ctx.fillText(`${pct}%`, 58 + BW + 8, y);
    ctx.fillStyle = "rgba(255,255,255,.07)"; rRect(ctx, 58, y+7, BW, 4, 2); ctx.fill();
    ctx.fillStyle = GOLD; rRect(ctx, 58, y+7, BW * pct/100, 4, 2); ctx.fill();
  };
  const drawDimR = (label, pct, y) => {
    const BW = 195, x = W - 58 - BW;
    ctx.fillStyle = "rgba(240,235,225,.45)"; ctx.font = '400 10px "Jost",Arial,sans-serif'; ctx.textAlign = "right";
    ctx.fillText(label, W - 58, y);
    ctx.fillStyle = GOLD; ctx.font = '600 10px "Jost",Arial,sans-serif'; ctx.textAlign = "left";
    ctx.fillText(`${pct}%`, x - 30, y);
    ctx.fillStyle = "rgba(255,255,255,.07)"; rRect(ctx, x, y+7, BW, 4, 2); ctx.fill();
    ctx.fillStyle = GOLD; rRect(ctx, x, y+7, BW * pct/100, 4, 2); ctx.fill();
  };

  drawDimL("SOUL RESONANCE",  dimScore("sky"),         218);
  drawDimL("LOVE ALIGNMENT",  dimScore("topRight"),    262);
  drawDimL("LIFE PURPOSE",    dimScore("earth"),       306);
  drawDimL("KARMIC BOND",     dimScore("water"),       350);
  drawDimR("DESTINY SYNC",    dimScore("fire"),        218);
  drawDimR("SPIRIT BOND",     dimScore("topLeft"),     262);
  drawDimR("ABUNDANCE ALIGN", dimScore("bottomRight"), 306);
  drawDimR("CORE HARMONY",    dimScore("center"),      350);

  const cx = W/2, cy = 370, R = 118;
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI*2);
  ctx.strokeStyle = "rgba(255,255,255,.06)"; ctx.lineWidth = 13; ctx.stroke();
  const scoreColor = score >= 75 ? "#7CFC00" : score >= 55 ? "#C9A465" : "#FF9BB0";
  ctx.beginPath(); ctx.arc(cx, cy, R, -Math.PI/2, -Math.PI/2 + (score/100)*Math.PI*2);
  ctx.strokeStyle = scoreColor; ctx.lineWidth = 13; ctx.lineCap = "round"; ctx.stroke();
  ctx.fillStyle = "#F0EBE1"; ctx.font = '300 76px "Cormorant Garamond",Georgia,serif'; ctx.textAlign = "center";
  ctx.fillText(`${score}%`, cx, cy + 22);
  ctx.fillStyle = "rgba(240,235,225,.4)"; ctx.font = '500 9px "Jost",Arial,sans-serif';
  ctx.fillText("COMPATIBILITY", cx, cy + 48);
  const lbl = score >= 80 ? "Twin Flame" : score >= 65 ? "Soul Mates" : score >= 50 ? "Deep Connection" : "Growth Partnership";
  ctx.fillStyle = scoreColor; ctx.font = '400 21px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(lbl, cx, cy + 78);

  ctx.fillStyle = "rgba(255,155,176,.65)"; ctx.font = '400 9px "Jost",Arial,sans-serif'; ctx.textAlign = "left";
  ctx.fillText(`${n1.toUpperCase()}  ·  CORE`, 58, 418);
  ctx.fillStyle = "#F0EBE1"; ctx.font = '400 17px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(`${mat1.center} · ${ARCANA[mat1.center]?.name}`, 58, 442);

  ctx.fillStyle = "rgba(201,164,101,.65)"; ctx.font = '400 9px "Jost",Arial,sans-serif'; ctx.textAlign = "right";
  ctx.fillText(`${n2.toUpperCase()}  ·  CORE`, W-58, 418);
  ctx.fillStyle = "#F0EBE1"; ctx.font = '400 17px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(`${mat2.center} · ${ARCANA[mat2.center]?.name}`, W-58, 442);

  ctx.strokeStyle = "rgba(201,164,101,.13)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(50, 480); ctx.lineTo(W-50, 480); ctx.stroke();
  ctx.fillStyle = "rgba(240,235,225,.18)"; ctx.font = '400 11px "Jost",Arial,sans-serif'; ctx.textAlign = "center";
  ctx.fillText("Discover your destiny at  aimatrixdestiny.com  ·  AI-Powered Matrix of Destiny Reading", W/2, 510);

  return canvas;
}

async function downloadCompatCard(mat1, mat2, fd1, fd2, score) {
  const canvas = await generateCompatCanvas(mat1, mat2, fd1, fd2, score);
  const n1 = fd1?.name || "Person 1", n2 = fd2?.name || "Person 2";
  const link = document.createElement("a");
  link.download = `compat-${n1.toLowerCase().replace(/\s+/g,"-")}-${n2.toLowerCase().replace(/\s+/g,"-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

async function generateResultsCanvas(mat, fd) {
  await document.fonts.ready;
  const W = 1080, H = 1080, SC = 2;
  const canvas = document.createElement("canvas");
  canvas.width = W * SC; canvas.height = H * SC;
  const ctx = canvas.getContext("2d");
  ctx.scale(SC, SC);

  ctx.fillStyle = "#05050E"; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, 500);
  glow.addColorStop(0, "rgba(91,63,166,.2)"); glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(201,164,101,.32)"; ctx.lineWidth = 1;
  ctx.strokeRect(22, 22, W-44, H-44);

  ctx.fillStyle = "#C9A465"; ctx.font = '600 11px "Jost",Arial,sans-serif'; ctx.textAlign = "center";
  ctx.fillText("✦  AI MATRIX DESTINY  ✦", W/2, 65);

  const name = fd?.name ? `${fd.name}'s` : "Your";
  ctx.fillStyle = "#F0EBE1"; ctx.font = '300 60px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(`${name} Sacred Matrix`, W/2, 170);
  ctx.fillStyle = "rgba(240,235,225,.38)"; ctx.font = '400 15px "Jost",Arial,sans-serif';
  ctx.fillText(`Born ${fd.day}/${fd.month}/${fd.year}`, W/2, 205);

  ctx.strokeStyle = "rgba(201,164,101,.13)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(80, 228); ctx.lineTo(W-80, 228); ctx.stroke();

  const cx = W/2, cy = 480, IR = 70, OR = 232;
  const nodes = [
    { k:"sky",         l:"SOUL",      a:-90  },
    { k:"topRight",    l:"LOVE",      a:-45  },
    { k:"fire",        l:"DESTINY",   a:0    },
    { k:"bottomRight", l:"ABUNDANCE", a:45   },
    { k:"earth",       l:"PURPOSE",   a:90   },
    { k:"bottomLeft",  l:"HEALTH",    a:135  },
    { k:"water",       l:"KARMA",     a:180  },
    { k:"topLeft",     l:"SPIRIT",    a:-135 },
  ];

  for (const n of nodes) {
    const rad = n.a * Math.PI / 180;
    const nx = cx + Math.cos(rad) * OR, ny = cy + Math.sin(rad) * OR;
    ctx.beginPath(); ctx.moveTo(cx + Math.cos(rad)*(IR+5), cy + Math.sin(rad)*(IR+5));
    ctx.lineTo(nx - Math.cos(rad)*42, ny - Math.sin(rad)*42);
    ctx.strokeStyle = "rgba(201,164,101,.1)"; ctx.lineWidth = 1; ctx.stroke();
    ctx.beginPath(); ctx.arc(nx, ny, 40, 0, Math.PI*2);
    ctx.fillStyle = "#0C0C1E"; ctx.fill();
    ctx.strokeStyle = "rgba(201,164,101,.5)"; ctx.lineWidth = 1.2; ctx.stroke();
    ctx.fillStyle = "#E8C97A"; ctx.font = '600 22px "Cormorant Garamond",Georgia,serif'; ctx.textAlign = "center";
    ctx.fillText(mat[n.k], nx, ny + 6);
    ctx.fillStyle = "rgba(240,235,225,.32)"; ctx.font = '500 7px "Jost",Arial,sans-serif';
    ctx.fillText(n.l, nx, ny + 24);
  }

  ctx.beginPath(); ctx.arc(cx, cy, IR, 0, Math.PI*2);
  ctx.fillStyle = "#0C0C1E"; ctx.fill();
  ctx.strokeStyle = "#C9A465"; ctx.lineWidth = 1.8; ctx.stroke();
  ctx.fillStyle = "#E8C97A"; ctx.font = '600 46px "Cormorant Garamond",Georgia,serif'; ctx.textAlign = "center";
  ctx.fillText(mat.center, cx, cy + 14);

  const innerOR = OR * 0.44;
  const innerNodes = [
    { k:"comfort", l:"COMFORT", a:-90 },
    { k:"social",  l:"SOCIAL",  a:90  },
  ];
  for (const n of innerNodes) {
    const rad = n.a * Math.PI / 180;
    const nx = cx + Math.cos(rad) * innerOR, ny = cy + Math.sin(rad) * innerOR;
    ctx.beginPath(); ctx.arc(nx, ny, 24, 0, Math.PI*2);
    ctx.fillStyle = "#0C0C1E"; ctx.fill();
    ctx.strokeStyle = "rgba(184,115,51,.7)"; ctx.lineWidth = 1.2; ctx.stroke();
    ctx.fillStyle = "#E8C97A"; ctx.font = '600 15px "Cormorant Garamond",Georgia,serif'; ctx.textAlign = "center";
    ctx.fillText(mat[n.k], nx, ny + 5);
    ctx.fillStyle = "rgba(240,235,225,.3)"; ctx.font = '500 6px "Jost",Arial,sans-serif';
    ctx.fillText(n.l, nx, ny + 18);
  }

  ctx.fillStyle = "rgba(240,235,225,.5)"; ctx.font = '300 italic 20px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(ARCANA[mat.center]?.name, cx, cy + OR + 60);
  ctx.fillStyle = "rgba(240,235,225,.3)"; ctx.font = '300 italic 16px "Cormorant Garamond",Georgia,serif';
  ctx.fillText(ARCANA[mat.center]?.theme, cx, cy + OR + 88);

  ctx.strokeStyle = "rgba(201,164,101,.13)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(80, H-115); ctx.lineTo(W-80, H-115); ctx.stroke();
  ctx.fillStyle = "rgba(240,235,225,.18)"; ctx.font = '400 11px "Jost",Arial,sans-serif';
  ctx.fillText("Discover your destiny at  aimatrixdestiny.com", W/2, H-80);

  return canvas;
}

async function downloadResultsCard(mat, fd) {
  const canvas = await generateResultsCanvas(mat, fd);
  const link = document.createElement("a");
  link.download = `matrix-${(fd.name||"reading").toLowerCase().replace(/\s+/g,"-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

async function _shareCard(canvas, filename, shareType, text) {
  const siteUrl = "https://aimatrixdestiny.com";
  if (shareType === "whatsapp") {
    const msg = encodeURIComponent(`${text}\n${siteUrl}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
    return;
  }
  if (shareType === "instagram") {
    if (navigator.canShare) {
      try {
        const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "AI Matrix Destiny", text });
          return;
        }
      } catch {}
    }
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
    setTimeout(() => alert("Image saved! Open Instagram and post it from your camera roll."), 400);
  }
}

async function shareResultsCard(mat, fd, shareType) {
  const canvas = await generateResultsCanvas(mat, fd);
  const filename = `matrix-${(fd.name||"reading").toLowerCase().replace(/\s+/g,"-")}.png`;
  const arcanaName = ARCANA[mat.center]?.name || "";
  const text = `I just discovered my Destiny Matrix! My core arcana is ${mat.center}${arcanaName ? ` — the ${arcanaName}` : ""}. Discover yours at`;
  await _shareCard(canvas, filename, shareType, text);
}

async function shareCompatCard(mat1, mat2, fd1, fd2, score, shareType) {
  const canvas = await generateCompatCanvas(mat1, mat2, fd1, fd2, score);
  const n1 = fd1?.name || "Person 1", n2 = fd2?.name || "Person 2";
  const filename = `compat-${n1.toLowerCase().replace(/\s+/g,"-")}-${n2.toLowerCase().replace(/\s+/g,"-")}.png`;
  const text = `${n1} & ${n2} scored ${score}% compatibility on AI Matrix Destiny! ✦ Find out your soul connection at`;
  await _shareCard(canvas, filename, shareType, text);
}

/* ─────────────────────────────────────────────
   PDF PRINT FUNCTIONS
───────────────────────────────────────────── */
function printReadingPDF(mat, fd, interp) {
  const positions = [
    { k:"center",      l:"Core Essence" },
    { k:"sky",         l:"Soul Energy" },
    { k:"topRight",    l:"Love & Relationships" },
    { k:"fire",        l:"Destiny / Life Path" },
    { k:"earth",       l:"Life Purpose" },
    { k:"water",       l:"Karmic Root" },
    { k:"topLeft",     l:"Spirit & Talent" },
    { k:"bottomLeft",  l:"Health & Body" },
    { k:"bottomRight", l:"Material Abundance" },
  ];
  const posRows = positions.map(({ k, l }) => {
    const n = mat[k]; const a = ARCANA[n];
    return `<tr><td class="pl">${l}</td><td class="pn">${n}</td><td class="pa">${a?.name||""}</td><td class="pt">${a?.theme||""}</td></tr>`;
  }).join("");
  const staglist = (interp?.strengths || []).map(s => `<span class="tag">${s}</span>`).join("");
  const clist    = (interp?.challenges || []).map(c => `<li>${c}</li>`).join("");
  const dateStr  = new Date().toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" });
  const coreA        = ARCANA[mat.center];
  const loveA        = ARCANA[mat.topRight];
  const careerA      = ARCANA[mat.fire];
  const abundanceA   = ARCANA[mat.bottomRight];
  const purposeA     = ARCANA[mat.earth];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:"EB Garamond","Cormorant Garamond",Georgia,serif;background:#FBF8F3;color:#1A1208;font-size:13pt;line-height:1.75;padding:0}
    .page{max-width:820px;margin:0 auto;padding:48px 60px}
    .hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #8B6914;margin-bottom:30px}
    .brand{font-family:"Cormorant Garamond",serif;font-size:22pt;font-weight:500;color:#8B6914;letter-spacing:.02em}
    .brand-sub{font-size:8pt;letter-spacing:3px;text-transform:uppercase;color:#A0895C;margin-top:3px}
    .rtype{text-align:right;font-size:8.5pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914}
    .rdate{font-size:9pt;color:#8B6914;opacity:.55;margin-top:5px}
    .hero{text-align:center;padding:24px 0 22px;border-bottom:1px solid #D4B896;margin-bottom:28px}
    .hname{font-family:"Cormorant Garamond",serif;font-size:32pt;font-weight:300;color:#1A1208;margin-bottom:8px}
    .hborn{font-size:10pt;color:#6B5533;letter-spacing:2.5px;text-transform:uppercase}
    .hcore{display:inline-block;margin-top:16px;padding:10px 28px;border:1px solid #8B6914;border-radius:3px;break-inside:avoid}
    .hcore-n{font-family:"Cormorant Garamond",serif;font-size:28pt;font-weight:300;color:#8B6914;line-height:1}
    .hcore-name{font-size:9.5pt;letter-spacing:2px;text-transform:uppercase;color:#A0895C;margin-top:5px}
    .hcore-theme{font-size:10pt;color:#6B5533;font-style:italic;margin-top:3px}
    .sec{margin-bottom:26px}
    .sec-title{font-family:"Cormorant Garamond",serif;font-size:17pt;font-weight:400;color:#8B6914;border-bottom:1px solid #D4B896;padding-bottom:7px;margin-bottom:14px;letter-spacing:.02em;break-after:avoid;page-break-after:avoid}
    .summary{font-family:"Cormorant Garamond",serif;font-size:13.5pt;font-weight:300;font-style:italic;color:#2A2010;line-height:1.82;padding:14px 20px;border-left:3px solid #8B6914;background:rgba(139,105,20,.04);margin-bottom:4px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .body-text{font-size:12pt;color:#2A2010;line-height:1.82;margin-bottom:12px}
    table{width:100%;border-collapse:collapse;font-size:11pt}
    th{text-align:left;font-size:7.5pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914;border-bottom:1px solid #D4B896;padding:5px 8px;font-family:inherit}
    td{padding:6px 8px;border-bottom:1px solid rgba(212,184,150,.28);vertical-align:top}
    .pl{color:#6B5533;font-size:10pt;width:36%}
    .pn{color:#8B6914;font-family:"Cormorant Garamond",serif;font-size:15pt;width:8%;text-align:center}
    .pa{color:#1A1208;font-weight:500;width:28%}
    .pt{color:#8B6914;font-style:italic;font-size:10pt;width:28%}
    .twocol{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:20px;break-inside:avoid;page-break-inside:avoid}
    .col-h{font-family:"Cormorant Garamond",serif;font-size:13pt;color:#8B6914;margin-bottom:10px;font-weight:400}
    .tags{display:flex;flex-wrap:wrap;gap:7px;margin-top:2px}
    .tag{display:inline-block;padding:3px 12px;border:1px solid #8B6914;border-radius:100px;font-size:9pt;color:#5A3A00;background:#F5E8CE;-webkit-print-color-adjust:exact;print-color-adjust:exact;letter-spacing:.01em;font-family:"EB Garamond",Georgia,serif}
    ul{padding-left:17px}
    ul li{margin-bottom:6px;font-size:12pt;color:#2A2010;line-height:1.6}
    ul li::marker{color:#8B6914}
    .affirm{padding:14px 20px;border:1px solid #8B6914;border-radius:3px;background:rgba(139,105,20,.03);text-align:center;margin-top:16px;break-inside:avoid;page-break-inside:avoid}
    .affirm-lbl{font-size:8pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914;margin-bottom:7px}
    .affirm-txt{font-family:"Cormorant Garamond",serif;font-size:15pt;font-weight:300;font-style:italic;color:#1A1208;line-height:1.65}
    .arcana-pair{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:14px;break-inside:avoid;page-break-inside:avoid}
    .arcana-box{border:1px solid #D4B896;border-radius:4px;padding:14px 16px;background:rgba(139,105,20,.03);-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .arcana-lbl{font-size:7.5pt;letter-spacing:2px;text-transform:uppercase;color:#A0895C;margin-bottom:6px}
    .arcana-num{font-family:"Cormorant Garamond",serif;font-size:26pt;font-weight:300;color:#8B6914;line-height:1;margin-bottom:3px}
    .arcana-name{font-size:12pt;font-weight:500;color:#1A1208;margin-bottom:2px}
    .arcana-theme{font-size:10pt;color:#6B5533;font-style:italic}
    .ft{margin-top:36px;padding-top:16px;border-top:1px solid #D4B896;display:flex;justify-content:space-between;align-items:center}
    .ft-brand{font-family:"Cormorant Garamond",serif;font-size:14pt;color:#8B6914}
    .ft-note{font-size:8pt;color:#A0895C;text-align:right;max-width:340px;line-height:1.6}
    @page{margin:14mm 12mm}
    @media print{body{background:#fff;margin:0}.page{padding:0}.tag{background:#F5E8CE!important;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>AI Matrix Destiny — ${fd?.name || "Reading"}</title>
<style>${css}</style></head><body><div class="page">
<div class="hdr">
  <div><div class="brand">✦ AI Matrix Destiny</div><div class="brand-sub">Personal Matrix Reading</div></div>
  <div><div class="rtype">Sacred Matrix Report</div><div class="rdate">Generated ${dateStr}</div></div>
</div>
<div class="hero">
  <div class="hname">${fd?.name || "Your"}'s Sacred Matrix</div>
  <div class="hborn">Born &nbsp;${fd?.day} / ${fd?.month} / ${fd?.year}</div>
  <div class="hcore">
    <div class="hcore-n">${mat.center}</div>
    <div class="hcore-name">Core Essence · ${coreA?.name || ""}</div>
    <div class="hcore-theme">${coreA?.theme || ""}</div>
  </div>
</div>
${interp ? `<div class="sec">
  <div class="sec-title">Soul Blueprint Overview</div>
  <div class="summary">${interp.summary}</div>
</div>` : ""}
<div class="sec">
  <div class="sec-title">Matrix Positions</div>
  <table><thead><tr><th>Position</th><th>#</th><th>Arcana</th><th>Theme</th></tr></thead>
  <tbody>${posRows}</tbody></table>
</div>
${interp ? `<div class="sec">
  <div class="sec-title">Personality Profile</div>
  <p class="body-text">${interp.personality}</p>
  <div class="twocol">
    <div><div class="col-h">Natural Strengths</div><div class="tags">${staglist}</div></div>
    <div><div class="col-h">Growth Areas</div><ul>${clist}</ul></div>
  </div>
  <div class="affirm">
    <div class="affirm-lbl">Personal Affirmation</div>
    <div class="affirm-txt">"${interp.affirmation}"</div>
  </div>
</div>
<div class="sec">
  <div class="sec-title">Love &amp; Relationship Blueprint</div>
  <p class="body-text">${interp.love}</p>
  <p style="font-size:10.5pt;color:#6B5533;margin-top:10px;">Love Arcana: <strong>${mat.topRight} · ${loveA?.name || ""}</strong> — ${loveA?.theme || ""}</p>
</div>
${interp.career ? `<div class="sec">
  <div class="sec-title">Career Matrix</div>
  <p class="body-text">${interp.career}</p>
  <div class="arcana-pair">
    <div class="arcana-box">
      <div class="arcana-lbl">Career Destiny Arcana</div>
      <div class="arcana-num">${mat.fire}</div>
      <div class="arcana-name">${careerA?.name || ""}</div>
      <div class="arcana-theme">${careerA?.theme || ""}</div>
    </div>
    <div class="arcana-box">
      <div class="arcana-lbl">Financial Blueprint</div>
      <div class="arcana-num">${mat.bottomRight}</div>
      <div class="arcana-name">${abundanceA?.name || ""}</div>
      <div class="arcana-theme">${abundanceA?.theme || ""}</div>
    </div>
  </div>
</div>` : ""}
${interp.business ? `<div class="sec">
  <div class="sec-title">Business Matrix</div>
  <p class="body-text">${interp.business}</p>
  <div class="arcana-pair">
    <div class="arcana-box">
      <div class="arcana-lbl">Business Mission Arcana</div>
      <div class="arcana-num">${mat.earth}</div>
      <div class="arcana-name">${purposeA?.name || ""}</div>
      <div class="arcana-theme">${purposeA?.theme || ""}</div>
    </div>
    <div class="arcana-box">
      <div class="arcana-lbl">Leadership Authority</div>
      <div class="arcana-num">${mat.center}</div>
      <div class="arcana-name">${coreA?.name || ""}</div>
      <div class="arcana-theme">${coreA?.theme || ""}</div>
    </div>
  </div>
</div>` : ""}
<div class="sec">
  <div class="sec-title">${new Date().getFullYear()} Energy Forecast</div>
  <p class="body-text">${interp.yearPrediction}</p>
</div>` : ""}
<div class="ft">
  <div class="ft-brand">✦ AI Matrix Destiny</div>
  <div class="ft-note">aimatrixdestiny.com · For entertainment &amp; self-reflection purposes only. Not a substitute for professional advice.</div>
</div>
</div></body></html>`;

  const w = window.open("", "_blank", "width=920,height=820");
  if (!w) { alert("Please allow pop-ups to save the PDF."); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 900);
}

function printCompatPDF(mat1, mat2, fd1, fd2, compat, score) {
  const n1 = fd1?.name || "Person 1", n2 = fd2?.name || "Person 2";
  const scoreLabel = score >= 80 ? "Twin Flame" : score >= 65 ? "Soul Mates" : score >= 50 ? "Deep Connection" : "Growth Partnership";
  const staglist = (compat?.strengths || []).map(s => `<span class="tag">${s}</span>`).join("");
  const clist    = (compat?.challenges || []).map(c => `<li>${c}</li>`).join("");
  const dateStr  = new Date().toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" });

  const compPositions = [
    { k:"sky", l:"Soul" }, { k:"topRight", l:"Love" }, { k:"fire", l:"Destiny" },
    { k:"earth", l:"Purpose" }, { k:"water", l:"Karma" }, { k:"center", l:"Core" },
  ];
  const posRows = compPositions.map(({ k, l }) => {
    const s = Math.round(Math.max(0, 1 - Math.abs(mat1[k] - mat2[k]) / 11) * 100);
    const col = s >= 75 ? "#2A6B2A" : s >= 50 ? "#8B6914" : "#8B2A2A";
    return `<tr>
      <td class="pl">${l}</td>
      <td class="pc1">${mat1[k]} · ${ARCANA[mat1[k]]?.name || ""}</td>
      <td class="pc2">${mat2[k]} · ${ARCANA[mat2[k]]?.name || ""}</td>
      <td class="ps" style="color:${col}">${s}%</td>
    </tr>`;
  }).join("");

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:"EB Garamond","Cormorant Garamond",Georgia,serif;background:#FBF8F3;color:#1A1208;font-size:13pt;line-height:1.75;padding:0}
    .page{max-width:820px;margin:0 auto;padding:48px 60px}
    .hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #8B6914;margin-bottom:28px}
    .brand{font-family:"Cormorant Garamond",serif;font-size:22pt;font-weight:500;color:#8B6914;letter-spacing:.02em}
    .brand-sub{font-size:8pt;letter-spacing:3px;text-transform:uppercase;color:#A0895C;margin-top:3px}
    .rtype{text-align:right;font-size:8.5pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914}
    .rdate{font-size:9pt;color:#8B6914;opacity:.55;margin-top:5px}
    .hero{text-align:center;padding:24px 0 22px;border-bottom:1px solid #D4B896;margin-bottom:28px}
    .hnames{font-family:"Cormorant Garamond",serif;font-size:28pt;font-weight:300;color:#1A1208;margin-bottom:8px}
    .hdates{font-size:10pt;color:#6B5533;letter-spacing:2px}
    .hscore{display:inline-block;margin-top:16px;padding:12px 32px;border:1px solid #8B6914;border-radius:3px;break-inside:avoid}
    .hscore-n{font-family:"Cormorant Garamond",serif;font-size:34pt;font-weight:300;color:#8B6914;line-height:1}
    .hscore-lbl{font-size:9pt;letter-spacing:2px;text-transform:uppercase;color:#A0895C;margin-top:5px}
    .hscore-cat{font-family:"Cormorant Garamond",serif;font-size:15pt;color:#6B5533;font-style:italic;margin-top:4px}
    .sec{margin-bottom:26px}
    .sec-title{font-family:"Cormorant Garamond",serif;font-size:17pt;font-weight:400;color:#8B6914;border-bottom:1px solid #D4B896;padding-bottom:7px;margin-bottom:14px;break-after:avoid;page-break-after:avoid}
    .bond{font-family:"Cormorant Garamond",serif;font-size:13.5pt;font-weight:300;font-style:italic;color:#2A2010;line-height:1.82;padding:14px 20px;border-left:3px solid #8B6914;background:rgba(139,105,20,.04);margin-bottom:4px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .body-text{font-size:12pt;color:#2A2010;line-height:1.82;margin-bottom:12px}
    .twocol{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:20px;break-inside:avoid;page-break-inside:avoid}
    .col-h{font-family:"Cormorant Garamond",serif;font-size:13pt;color:#8B6914;margin-bottom:10px;font-weight:400}
    .tags{display:flex;flex-wrap:wrap;gap:7px;margin-top:2px}
    .tag{display:inline-block;padding:3px 12px;border:1px solid #8B6914;border-radius:100px;font-size:9pt;color:#5A3A00;background:#F5E8CE;-webkit-print-color-adjust:exact;print-color-adjust:exact;letter-spacing:.01em;font-family:"EB Garamond",Georgia,serif}
    ul{padding-left:17px}
    ul li{margin-bottom:6px;font-size:12pt;color:#2A2010;line-height:1.6}
    ul li::marker{color:#8B6914}
    .karmic{padding:14px 20px;border-left:3px solid #6B3B8A;background:rgba(107,59,138,.04);margin-bottom:14px;font-family:"Cormorant Garamond",serif;font-size:13pt;font-style:italic;color:#2A2010;line-height:1.8;break-inside:avoid;page-break-inside:avoid;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .advice{padding:14px 20px;border:1px solid #8B6914;border-radius:3px;background:rgba(139,105,20,.03);text-align:center;margin-top:4px;break-inside:avoid;page-break-inside:avoid}
    .advice-lbl{font-size:8pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914;margin-bottom:7px}
    .advice-txt{font-family:"Cormorant Garamond",serif;font-size:15pt;font-weight:300;font-style:italic;color:#1A1208;line-height:1.65}
    table{width:100%;border-collapse:collapse;font-size:11pt}
    th{text-align:left;font-size:7.5pt;letter-spacing:2px;text-transform:uppercase;color:#8B6914;border-bottom:1px solid #D4B896;padding:5px 8px;font-family:inherit}
    td{padding:6px 8px;border-bottom:1px solid rgba(212,184,150,.28);vertical-align:top}
    .pl{color:#6B5533;font-size:10pt;width:14%}
    .pc1{color:#6B3B5A;font-size:11pt;width:36%}
    .pc2{color:#3B5A6B;font-size:11pt;width:36%}
    .ps{font-weight:500;font-size:11pt;width:14%;text-align:center}
    .ft{margin-top:36px;padding-top:16px;border-top:1px solid #D4B896;display:flex;justify-content:space-between;align-items:center}
    .ft-brand{font-family:"Cormorant Garamond",serif;font-size:14pt;color:#8B6914}
    .ft-note{font-size:8pt;color:#A0895C;text-align:right;max-width:340px;line-height:1.6}
    .upsell{page-break-before:always;background:#05050E;color:#F0EBE1;text-align:center;padding:56px 48px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .up-brand{font-size:9pt;letter-spacing:3px;text-transform:uppercase;color:#C9A465;margin-bottom:18px}
    .up-h{font-family:"Cormorant Garamond",serif;font-size:28pt;font-weight:300;color:#F0EBE1;line-height:1.2;margin-bottom:14px}
    .up-sub{font-size:12pt;color:rgba(240,235,225,.6);line-height:1.75;margin-bottom:32px;max-width:560px;margin-left:auto;margin-right:auto}
    .up-cards{display:flex;gap:16px;justify-content:center;flex-wrap:wrap;margin-bottom:34px}
    .up-card{border:1px solid rgba(201,164,101,.35);border-radius:6px;padding:22px 20px;width:176px;text-align:center;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .up-card.featured{border-color:#C9A465;border-width:2px;background:#1A1308;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .up-card-lbl{font-size:8pt;letter-spacing:2px;text-transform:uppercase;color:#C9A465;margin-bottom:6px}
    .up-badge{font-size:7pt;letter-spacing:1px;text-transform:uppercase;color:#8B6914;background:#F5E8CE;padding:2px 8px;border-radius:100px;display:inline-block;margin-bottom:8px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .up-price{font-family:"Cormorant Garamond",serif;font-size:28pt;color:#C9A465;font-weight:300;line-height:1.1;margin-bottom:4px}
    .up-freq{font-size:9pt;color:rgba(240,235,225,.5);margin-bottom:12px}
    .up-features{font-size:10pt;color:rgba(240,235,225,.7);line-height:1.75;text-align:left}
    .up-cta{border-top:1px solid rgba(201,164,101,.3);padding-top:24px}
    .up-cta-h{font-family:"Cormorant Garamond",serif;font-size:16pt;color:#F0EBE1;margin-bottom:8px}
    .up-url{font-size:12pt;color:#C9A465;letter-spacing:1px}
    @page{margin:14mm 12mm}
    @media print{body{background:#fff;margin:0}.page{padding:0}.tag{background:#F5E8CE!important;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}.upsell{background:#05050E!important;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}.up-card.featured{background:#1A1308!important}.up-badge{background:#F5E8CE!important;color:#8B6914!important}}
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>AI Matrix Destiny — ${n1} &amp; ${n2} Compatibility</title>
<style>${css}</style></head><body><div class="page">
<div class="hdr">
  <div><div class="brand">✦ AI Matrix Destiny</div><div class="brand-sub">Love Compatibility Reading</div></div>
  <div><div class="rtype">Soul Compatibility Report</div><div class="rdate">Generated ${dateStr}</div></div>
</div>
<div class="hero">
  <div class="hnames">${n1} &nbsp;♡&nbsp; ${n2}</div>
  <div class="hdates">${fd1?.day}/${fd1?.month}/${fd1?.year} &nbsp;·&nbsp; ${fd2?.day}/${fd2?.month}/${fd2?.year}</div>
  <div class="hscore">
    <div class="hscore-n">${score}%</div>
    <div class="hscore-lbl">Compatibility</div>
    <div class="hscore-cat">${scoreLabel}</div>
  </div>
</div>
${compat ? `
<div class="sec">
  <div class="sec-title">Soul Bond Overview</div>
  <div class="bond">${compat.bond}</div>
</div>
<div class="sec">
  <div class="sec-title">Love Dynamic</div>
  <p class="body-text">${compat.dynamic}</p>
</div>
<div class="sec">
  <div class="sec-title">Relationship Profile</div>
  <div class="twocol">
    <div><div class="col-h">Relationship Strengths</div><div class="tags">${staglist}</div></div>
    <div><div class="col-h">Growth Areas</div><ul>${clist}</ul></div>
  </div>
</div>
<div class="sec">
  <div class="sec-title">Karmic Contract</div>
  <div class="karmic">${compat.karmic}</div>
  <div class="advice">
    <div class="advice-lbl">Cosmic Advice for This Couple</div>
    <div class="advice-txt">"${compat.advice}"</div>
  </div>
</div>` : ""}
<div class="sec">
  <div class="sec-title">Position-by-Position Comparison</div>
  <table><thead><tr>
    <th>Position</th><th>${n1}</th><th>${n2}</th><th>Align</th>
  </tr></thead><tbody>${posRows}</tbody></table>
</div>
<div class="ft">
  <div class="ft-brand">✦ AI Matrix Destiny</div>
  <div class="ft-note">aimatrixdestiny.com · For entertainment &amp; self-reflection purposes only. Not a substitute for professional advice.</div>
</div>
</div>

<div class="upsell">
  <div class="up-brand">✦ AI Matrix Destiny ✦</div>
  <div class="up-h">Unlock Your Complete Sacred Blueprint</div>
  <div class="up-sub">Your compatibility reading is just the beginning. Discover the full depth of each person's individual matrix — personality, karma, life purpose, career path, and personalised monthly forecasts.</div>
  <div class="up-cards">
    <div class="up-card">
      <div class="up-card-lbl">Personal Matrix</div>
      <div class="up-price">$9</div>
      <div class="up-freq">One-time reading</div>
      <div class="up-features">Full personality report<br/>Love blueprint<br/>Career matrix<br/>Year energy forecast</div>
    </div>
    <div class="up-card featured">
      <div class="up-card-lbl">Full Bundle</div>
      <div class="up-badge">Most Popular</div>
      <div class="up-price">$19</div>
      <div class="up-freq">Both people's reading</div>
      <div class="up-features">Everything in Personal<br/>+ Partner's full matrix<br/>+ Downloadable PDFs<br/>+ Monthly forecasts</div>
    </div>
    <div class="up-card">
      <div class="up-card-lbl">Monthly Insights</div>
      <div class="up-price">$19<span style="font-size:14pt">/mo</span></div>
      <div class="up-freq">Ongoing guidance</div>
      <div class="up-features">AI monthly forecasts<br/>Unlimited readings<br/>Relationship guidance<br/>Priority support</div>
    </div>
  </div>
  <div class="up-cta">
    <div class="up-cta-h">Begin your deeper journey today</div>
    <div class="up-url">aimatrixdestiny.com</div>
  </div>
</div>

</body></html>`;

  const w = window.open("", "_blank", "width=920,height=820");
  if (!w) { alert("Please allow pop-ups to save the PDF."); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 900);
}

/* ─────────────────────────────────────────────
   STARS COMPONENT
───────────────────────────────────────────── */
function Stars({ n = 80 }) {
  const stars = useRef(
    Array.from({ length: n }, () => ({
      x: Math.random() * 100, y: Math.random() * 100,
      s: Math.random() * 1.8 + 0.4,
      d: Math.random() * 4 + 2,
      dl: Math.random() * 6,
    }))
  ).current;
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
      {stars.map((s, i) => (
        <div key={i} style={{
          position:"absolute", left:`${s.x}%`, top:`${s.y}%`,
          width:s.s, height:s.s, borderRadius:"50%", background:"#fff",
          animation:`twinkle ${s.d}s ${s.dl}s infinite ease-in-out`,
        }}/>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   LOCKED TAB PAYWALL WRAPPER
───────────────────────────────────────────── */
function LockedTab({ icon, title, desc, go, children, isPro }) {
  if (isPro) {
    return <div>{children}</div>;
  }
  return (
    <div style={{ position:"relative", minHeight:260 }}>
      <div style={{ filter:"blur(6px)", pointerEvents:"none", userSelect:"none", opacity:.88 }}>
        {children}
      </div>
      <div style={{
        position:"absolute", inset:0,
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        background:"radial-gradient(ellipse at 50% 40%, rgba(5,5,14,.05) 0%, rgba(5,5,14,.92) 58%)",
      }}>
        <div style={{ textAlign:"center", padding:"0 24px" }}>
          <div style={{ fontSize:30, color:"var(--gold)", marginBottom:10 }}>{icon}</div>
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--text)", marginBottom:8 }}>
            Unlock {title}
          </p>
          <p style={{ fontSize:13.5, color:"var(--text2)", marginBottom:20, maxWidth:290, lineHeight:1.65 }}>
            {desc}
          </p>
          <button className="gbtn" onClick={() => go("pricing")} style={{ fontSize:12.5, padding:"10px 26px" }}>
            Unlock Full Reading — From $9
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MONTHLY ENERGY FORECAST
───────────────────────────────────────────── */
function MonthlyForecast({ mat, fd }) {
  const reduceN = n => { while (n > 22) n = String(n).split("").reduce((a,b) => a + parseInt(b), 0); return n || 22; };
  const yr   = new Date().getFullYear();
  const nowM = new Date().getMonth() + 1;
  const pySum = `${fd.day}${fd.month}${yr}`.split("").reduce((a,b) => a + parseInt(b), 0);
  const py = reduceN(pySum);
  const mShort = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const mFull  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const months = Array.from({ length: 13 - nowM }, (_, i) => {
    const m = nowM + i;
    return { m, pm: reduceN(py + m), cur: i === 0 };
  });
  const thisMonth = months[0];
  const thisArc   = thisMonth ? ARCANA[thisMonth.pm] : null;
  return (
    <div className="glass" style={{ padding:26, marginBottom:22 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10, flexWrap:"wrap", gap:10 }}>
        <div>
          <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:400, color:"var(--text)", marginBottom:5 }}>
            Monthly Energy Forecast — {yr}
          </h3>
          <p style={{ fontSize:12.5, color:"var(--text2)" }}>
            Personal Year: <strong style={{ color:"var(--gold)" }}>{py} · {ARCANA[py]?.name}</strong> — {ARCANA[py]?.theme}
          </p>
        </div>
        <span style={{ padding:"3px 11px", background:"rgba(201,164,101,.13)", border:"1px solid rgba(201,164,101,.26)", borderRadius:100, fontSize:9.5, color:"var(--gold)", letterSpacing:"1px", whiteSpace:"nowrap" }}>NEW ✦</span>
      </div>
      <p style={{ fontSize:13.5, color:"rgba(240,235,225,.5)", lineHeight:1.72, marginBottom:20 }}>
        Each month activates a unique arcana frequency. Your personal year energy shapes how each monthly vibration manifests in your life.
      </p>
      <div style={{ display:"flex", gap:9, overflowX:"auto", paddingBottom:8, marginBottom:18 }}>
        {months.map(({ m, pm, cur }) => {
          const arc = ARCANA[pm];
          return (
            <div key={m} style={{
              flex:"0 0 auto", minWidth:100, padding:"13px 10px", borderRadius:11, textAlign:"center",
              background: cur ? "rgba(201,164,101,.1)" : "rgba(255,255,255,.025)",
              border:`1px solid ${cur ? "rgba(201,164,101,.42)" : "rgba(255,255,255,.06)"}`,
            }}>
              <div style={{ fontSize:8.5, color: cur ? "var(--gold)" : "rgba(240,235,225,.3)", letterSpacing:"2px", marginBottom:5 }}>
                {cur ? "▶ " : ""}{mShort[m-1]}
              </div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:29, fontWeight:300, color: cur ? "var(--gold)" : "var(--text)", lineHeight:1.1, marginBottom:4 }}>
                {pm}
              </div>
              <div style={{ fontSize:10.5, color: cur ? "rgba(201,164,101,.85)" : "rgba(240,235,225,.42)", lineHeight:1.4, marginBottom:3 }}>
                {arc?.name}
              </div>
              <div style={{ fontSize:9, color:"rgba(240,235,225,.28)", lineHeight:1.3 }}>
                {arc?.theme}
              </div>
            </div>
          );
        })}
      </div>
      {thisMonth && thisArc && (
        <div style={{ padding:"16px 18px", background:"rgba(201,164,101,.05)", borderRadius:11, border:"1px solid rgba(201,164,101,.16)" }}>
          <div style={{ fontSize:9, color:"var(--gold)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:6 }}>
            {mFull[nowM-1]} Energy — {thisArc.name}
          </div>
          <p style={{ fontSize:14, color:"rgba(240,235,225,.7)", lineHeight:1.75, marginBottom:12 }}>{thisArc.desc}</p>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {(thisArc.traits || []).map(t => (
              <span key={t} style={{ padding:"3px 10px", background:"rgba(201,164,101,.1)", border:"1px solid rgba(201,164,101,.2)", borderRadius:100, fontSize:11, color:"var(--gold)" }}>{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   OCTAGRAM CHART
───────────────────────────────────────────── */
function Chart({ mat, compact = false }) {
  const [hov, setHov] = useState(null);
  const S = compact ? 300 : 460;
  const C = S / 2;
  const R = compact ? 100 : 160;
  const r45 = R * 0.7071;

  const nodes = [
    { k:"sky",          x:C,       y:C-R,    label:"Soul" },
    { k:"topRight",     x:C+r45,   y:C-r45,  label:"Love" },
    { k:"fire",         x:C+R,     y:C,      label:"Destiny" },
    { k:"bottomRight",  x:C+r45,   y:C+r45,  label:"Abundance" },
    { k:"earth",        x:C,       y:C+R,    label:"Purpose" },
    { k:"bottomLeft",   x:C-r45,   y:C+r45,  label:"Health" },
    { k:"water",        x:C-R,     y:C,      label:"Karma" },
    { k:"topLeft",      x:C-r45,   y:C-r45,  label:"Spirit" },
  ];

  const sq1 = `${C},${C-R} ${C+R},${C} ${C},${C+R} ${C-R},${C}`;
  const sq2 = `${C+r45},${C-r45} ${C+r45},${C+r45} ${C-r45},${C+r45} ${C-r45},${C-r45}`;
  const nr = compact ? 20 : 28;
  const cr = compact ? 28 : 40;
  const fs = compact ? 12 : 17;

  return (
    <div style={{ position:"relative", width:"100%", maxWidth:S }}>
      <svg viewBox={`0 0 ${S} ${S}`} style={{ width:"100%", filter:"drop-shadow(0 0 32px rgba(201,164,101,.12))" }}>
        <defs>
          <filter id="glow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id="rbg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C9A465" stopOpacity=".1"/>
            <stop offset="100%" stopColor="#05050E" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="rcg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1A1640"/>
            <stop offset="100%" stopColor="#0C0C25"/>
          </radialGradient>
        </defs>
        <circle cx={C} cy={C} r={R+22} fill="url(#rbg)"/>
        <circle cx={C} cy={C} r={R+12} fill="none" stroke="rgba(201,164,101,.07)" strokeWidth="1" strokeDasharray="3 9"/>
        <polygon points={sq1} fill="rgba(201,164,101,.04)" stroke="#C9A465" strokeWidth="1.3" strokeOpacity=".5" filter="url(#glow)"/>
        <polygon points={sq2} fill="rgba(184,115,51,.03)" stroke="#B87333" strokeWidth="1.3" strokeOpacity=".4" filter="url(#glow)"/>
        {nodes.map(n => (
          <line key={n.k} x1={C} y1={C} x2={n.x} y2={n.y} stroke="rgba(201,164,101,.09)" strokeWidth=".7"/>
        ))}
        <circle cx={C} cy={C} r={R*.38} fill="none" stroke="rgba(201,164,101,.1)" strokeWidth=".7"/>
        {nodes.map(n => {
          const isH = hov === n.k;
          return (
            <g key={n.k} style={{ cursor:"pointer" }}
               onMouseEnter={() => setHov(n.k)} onMouseLeave={() => setHov(null)}>
              <circle cx={n.x} cy={n.y} r={nr+9} fill="rgba(201,164,101,.05)" opacity={isH ? 1 : 0}/>
              <circle cx={n.x} cy={n.y} r={nr} fill="url(#rcg)"
                stroke={isH ? "#E8C97A" : "#C9A465"} strokeWidth={isH ? 2 : 1.4} filter="url(#glow)"/>
              <text x={n.x} y={n.y} textAnchor="middle" dominantBaseline="central"
                fontSize={fs} fontFamily="Cormorant Garamond, serif" fontWeight="600" fill="#E8C97A">
                {mat[n.k]}
              </text>
              {!compact && (
                <text x={n.x} y={n.y + nr + 13} textAnchor="middle"
                  fontSize="7.5" fontFamily="Jost, sans-serif" fill="rgba(240,235,225,.38)" letterSpacing="1.5">
                  {n.label.toUpperCase()}
                </text>
              )}
            </g>
          );
        })}
        <circle cx={C} cy={C} r={cr+10} fill="rgba(201,164,101,.06)"/>
        <circle cx={C} cy={C} r={cr} fill="url(#rcg)"
          stroke="#C9A465" strokeWidth="2" filter="url(#glow)"/>
        <text x={C} y={C-(compact?0:4)} textAnchor="middle" dominantBaseline="central"
          fontSize={compact ? 18 : 24} fontFamily="Cormorant Garamond, serif" fontWeight="600" fill="#E8C97A">
          {mat.center}
        </text>
        {!compact && (
          <text x={C} y={C+17} textAnchor="middle"
            fontSize="6.5" fontFamily="Jost, sans-serif" fill="rgba(201,164,101,.6)" letterSpacing="2.5">
            CORE
          </text>
        )}
      </svg>
      {hov && (
        <div style={{
          position:"absolute", bottom:compact?0:-10, left:"50%", transform:"translateX(-50%)",
          background:"rgba(8,8,22,.96)", border:"1px solid rgba(201,164,101,.3)",
          borderRadius:10, padding:"10px 16px", minWidth:190,
          textAlign:"center", pointerEvents:"none", backdropFilter:"blur(20px)",
          animation:"fadeUp .18s ease",
        }}>
          <div style={{ fontSize:9, color:"var(--gold)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:3 }}>
            {nodes.find(n => n.k === hov)?.label}
          </div>
          <div style={{ fontSize:15, color:"var(--text)", fontFamily:"'Cormorant Garamond',serif", fontWeight:500 }}>
            {mat[hov]} — {ARCANA[mat[hov]]?.name}
          </div>
          <div style={{ fontSize:11, color:"var(--text2)", marginTop:2 }}>{ARCANA[mat[hov]]?.theme}</div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   NAVIGATION
───────────────────────────────────────────── */
function Nav({ page, go }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav style={{
      position:"fixed", top:0, left:0, right:0, zIndex:200,
      background: scrolled ? "rgba(5,5,14,.96)" : "transparent",
      backdropFilter: scrolled ? "blur(24px)" : "none",
      borderBottom: scrolled ? "1px solid rgba(201,164,101,.1)" : "none",
      transition:"all .3s ease", padding:"0 24px",
    }}>
      <div style={{ maxWidth:1160, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", height:68 }}>
        <div onClick={() => go("home")} style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
          <svg viewBox="0 0 38 38" width="34" height="34">
            <polygon points="19,3 24,14 35,14 26,22 29,33 19,27 9,33 12,22 3,14 14,14"
              fill="none" stroke="#C9A465" strokeWidth="1.6"/>
            <circle cx="19" cy="19" r="4.5" fill="#C9A465"/>
          </svg>
          <div>
            <div style={{ fontSize:16, fontFamily:"'Cormorant Garamond',serif", fontWeight:500, color:"var(--text)", lineHeight:1 }}>
              AI Matrix Destiny
            </div>
            <div style={{ fontSize:8, color:"rgba(201,164,101,.7)", letterSpacing:"2.5px", textTransform:"uppercase", marginTop:2 }}>
              Calculator
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:26 }}>
          {[["home","Home"],["calculator","Calculator"],["compatibility","Compatibility"],["blog","Blog"],["history","History"],["pricing","Pricing"]].map(([p,l]) => (
            <button key={p} onClick={() => go(p)} style={{
              background:"none", border:"none", cursor:"pointer", fontFamily:"Jost,sans-serif",
              fontSize:12.5, fontWeight:500, letterSpacing:".06em", textTransform:"uppercase",
              color: page===p ? "#C9A465" : "rgba(240,235,225,.5)", transition:"color .2s",
            }}>{l}</button>
          ))}
          <button className="gbtn" onClick={() => go("calculator")} style={{ padding:"10px 22px", fontSize:12 }}>
            Free Reading
          </button>
        </div>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────────── */
function Home({ go }) {
  const testimonials = [
    { n:"Sophia M.", l:"London",    t:"The reading described my inner conflicts and gifts with a depth I've never found anywhere else. The Love and Career sections were jaw-dropping accurate.", av:"S" },
    { n:"Marcus R.", l:"New York",  t:"I was a total skeptic. Fifteen minutes later I was emotional. It mapped my entire life story — things I've never told anyone.", av:"M" },
    { n:"Aisha B.",  l:"Dubai",     t:"Understanding my finance number finally explained why I've always struggled with certain money patterns. Genuinely life-changing insight.", av:"A" },
    { n:"Chen L.",   l:"Singapore", t:"I've tried countless personality tools. This is the first one that felt supernatural in its accuracy. Remarkable platform.", av:"C" },
  ];
  return (
    <div>
      {/* ── HERO ── */}
      <section style={{
        position:"relative", minHeight:"100vh", display:"flex", alignItems:"center",
        justifyContent:"center", overflow:"hidden", padding:"120px 24px 80px",
        background:"radial-gradient(ellipse at 50% -5%,rgba(91,63,166,.25) 0%,transparent 60%)",
      }}>
        <Stars n={100}/>
        <div style={{ position:"absolute", right:"-6%", top:"50%", transform:"translateY(-50%)", width:"52vw", maxWidth:660, opacity:.05, pointerEvents:"none" }}>
          <svg viewBox="0 0 500 500" style={{ animation:"spinR 140s linear infinite" }}>
            <polygon points="250,28 318,178 478,178 348,272 396,430 250,336 104,430 152,272 22,178 182,178"
              fill="none" stroke="#C9A465" strokeWidth="2"/>
          </svg>
        </div>
        <div style={{ maxWidth:820, textAlign:"center", position:"relative", zIndex:1, animation:"fadeUp .9s ease forwards" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(201,164,101,.1)", border:"1px solid rgba(201,164,101,.28)", borderRadius:100, padding:"6px 18px", marginBottom:30 }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:"#C9A465", display:"inline-block", animation:"pulse 2s infinite" }}/>
            <span style={{ fontSize:10.5, color:"#C9A465", fontWeight:600, letterSpacing:"2.5px", textTransform:"uppercase" }}>
              AI-Powered · Free Instant Reading
            </span>
          </div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(46px,7.5vw,90px)", fontWeight:300, lineHeight:1.08, letterSpacing:"-.02em", marginBottom:24, color:"var(--text)" }}>
            Discover Your<br/>
            <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A,#C9A465)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", fontStyle:"italic" }}>
              Destiny Matrix
            </span>
          </h1>
          <p style={{ fontSize:18, color:"var(--text2)", lineHeight:1.75, maxWidth:560, margin:"0 auto 42px", fontWeight:300 }}>
            Enter your date of birth and our AI reveals the sacred blueprint of your soul — personality, purpose, love, karma, and the hidden forces shaping your entire life.
          </p>
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:60 }}>
            <button className="gbtn" onClick={() => go("calculator")} style={{ fontSize:15, padding:"16px 44px" }}>
              Reveal My Matrix ✦
            </button>
            <button className="obtn" onClick={() => go("pricing")}>View Plans</button>
          </div>
          <div style={{ display:"flex", gap:"clamp(20px,5vw,52px)", justifyContent:"center", flexWrap:"wrap" }}>
            {[["2.4M+","Readings Generated"],["98%","Accuracy Rating"],["150+","Countries"],["22","Arcana System"]].map(([v,l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontSize:28, fontWeight:500, color:"var(--gold)", fontFamily:"'Cormorant Garamond',serif", lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:9.5, color:"rgba(240,235,225,.38)", letterSpacing:"2px", textTransform:"uppercase", marginTop:5 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding:"96px 24px", maxWidth:1120, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:56 }}>
          <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>The Process</p>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(30px,5vw,52px)", fontWeight:300, color:"var(--text)" }}>
            Your Reading in{" "}
            <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
              Three Steps
            </span>
          </h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:22 }}>
          {[
            { n:"01", ic:"◈", t:"Enter Your Birth Date",   d:"Simply input your date of birth — the precise cosmic timestamp of your soul's arrival. No other information is needed to begin your reading." },
            { n:"02", ic:"✦", t:"Matrix Is Calculated",    d:"Our engine instantly computes all 9 positions of your sacred Octagram using the authentic 22-arcana Destiny Matrix formula — deterministic and precise." },
            { n:"03", ic:"◉", t:"AI Reveals Your Blueprint",d:"Advanced AI interprets every position, weaving a deeply personalized portrait of your personality, purpose, love, karma, and life trajectory." },
          ].map((s, i) => (
            <div key={i} className="glass" style={{ padding:"30px 26px", position:"relative", overflow:"hidden", transition:"transform .25s" }}
              onMouseEnter={e => e.currentTarget.style.transform="translateY(-4px)"}
              onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
              <div style={{ position:"absolute", top:-10, right:10, fontFamily:"'Cormorant Garamond',serif", fontSize:88, fontWeight:300, color:"rgba(201,164,101,.055)", lineHeight:1, userSelect:"none" }}>{s.n}</div>
              <div style={{ width:44, height:44, background:"rgba(201,164,101,.09)", border:"1px solid rgba(201,164,101,.26)", borderRadius:11, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:"var(--gold)", marginBottom:18 }}>{s.ic}</div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:500, color:"var(--text)", marginBottom:10 }}>{s.t}</h3>
              <p style={{ color:"var(--text2)", lineHeight:1.78, fontSize:14 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SAMPLE CHART ── */}
      <section style={{ padding:"80px 24px", background:"linear-gradient(180deg,transparent,rgba(91,63,166,.06),transparent)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,370px),1fr))", gap:60, alignItems:"center" }}>
          <div>
            <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:14 }}>The Sacred Octagram</p>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,4.5vw,50px)", fontWeight:300, lineHeight:1.2, color:"var(--text)", marginBottom:18 }}>
              9 Positions.<br/>
              <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                Infinite Insight.
              </span>
            </h2>
            <p style={{ color:"var(--text2)", lineHeight:1.82, fontSize:14.5, marginBottom:26 }}>
              Your Destiny Matrix is a sacred 8-pointed star — each node revealing a different dimension of who you truly are. The center holds your core essence: the fundamental truth of your soul's journey.
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:30 }}>
              {[["✦ Soul","Day of birth — your authentic inner self"],["◈ Destiny","Year energy — your life direction"],["⟡ Purpose","Month — your sacred life mission"],["♡ Love","Your relationship patterns & soul needs"],["◉ Core","Synthesis of your entire destiny matrix"]].map(([l,d]) => (
                <div key={l} style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <span style={{ color:"var(--gold)", fontSize:12, minWidth:88, fontWeight:500 }}>{l}</span>
                  <span style={{ color:"rgba(240,235,225,.42)", fontSize:13 }}>{d}</span>
                </div>
              ))}
            </div>
            <button className="gbtn" onClick={() => go("calculator")}>Calculate Yours Free ✦</button>
          </div>
          <div style={{ display:"flex", justifyContent:"center" }}>
            <div style={{ width:"100%", maxWidth:400, paddingBottom:52 }}>
              <Chart mat={{ sky:7, earth:3, fire:19, water:10, topRight:17, bottomRight:3, bottomLeft:13, topLeft:5, center:11 }}/>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding:"88px 24px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>Why Choose Us</p>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,52px)", fontWeight:300, color:"var(--text)" }}>
              The Most{" "}
              <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                Precise
              </span>{" "}Platform
            </h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(236px,1fr))", gap:18 }}>
            {[
              { ic:"✦", t:"Authentic 22-Arcana Engine",   d:"Our proprietary calculation engine uses the genuine Matrix of Destiny methodology — not the simplified versions found on competing sites." },
              { ic:"◈", t:"Deep AI Interpretation",        d:"Advanced AI trained on arcana symbology generates warm, human-like insights that feel genuinely personalized to your exact life situation." },
              { ic:"⟡", t:"Full 9-Position Reading",       d:"Explore all 9 key matrix positions — Soul, Destiny, Purpose, Karma, Love, Finance, Health, Spirit, and Core Essence — in full detail." },
              { ic:"◉", t:"Premium PDF Reports",           d:"Download beautifully designed 40-page reports with your complete matrix analysis — perfect for deep reflection, journaling, or sharing." },
            ].map((f, i) => (
              <div key={i} className="glass" style={{ padding:26, transition:"transform .25s" }}
                onMouseEnter={e => e.currentTarget.style.transform="translateY(-4px)"}
                onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
                <div style={{ fontSize:24, color:"var(--gold)", marginBottom:14 }}>{f.ic}</div>
                <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:500, color:"var(--text)", marginBottom:9 }}>{f.t}</h3>
                <p style={{ color:"var(--text2)", lineHeight:1.78, fontSize:13.5 }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={{ padding:"88px 24px", background:"rgba(91,63,166,.04)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>Testimonials</p>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,52px)", fontWeight:300, color:"var(--text)" }}>
              Lives{" "}
              <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                Transformed
              </span>
            </h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:18 }}>
            {testimonials.map((t, i) => (
              <div key={i} className="glass" style={{ padding:26 }}>
                <div style={{ display:"flex", gap:2, marginBottom:12 }}>
                  {[1,2,3,4,5].map(j => <span key={j} style={{ color:"var(--gold)", fontSize:13 }}>★</span>)}
                </div>
                <p style={{ color:"rgba(240,235,225,.7)", lineHeight:1.82, fontSize:14, marginBottom:18, fontStyle:"italic" }}>"{t.t}"</p>
                <div style={{ display:"flex", alignItems:"center", gap:11 }}>
                  <div style={{ width:34, height:34, borderRadius:"50%", background:"rgba(201,164,101,.14)", border:"1px solid rgba(201,164,101,.33)", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--gold)", fontWeight:600, fontSize:13 }}>{t.av}</div>
                  <div>
                    <div style={{ color:"var(--text)", fontWeight:500, fontSize:13.5 }}>{t.n}</div>
                    <div style={{ color:"rgba(240,235,225,.36)", fontSize:11.5 }}>{t.l}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding:"120px 24px", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 50%,rgba(201,164,101,.065) 0%,transparent 60%)", pointerEvents:"none" }}/>
        <div style={{ maxWidth:560, margin:"0 auto", position:"relative", zIndex:1 }}>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(36px,6vw,66px)", fontWeight:300, lineHeight:1.12, color:"var(--text)", marginBottom:18 }}>
            Ready to Discover<br/>
            <span style={{ background:"linear-gradient(135deg,#C9A465,#E8C97A)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
              Your Destiny?
            </span>
          </h2>
          <p style={{ color:"var(--text2)", fontSize:16, lineHeight:1.72, marginBottom:38 }}>Your free reading takes under 60 seconds. No credit card required.</p>
          <button className="gbtn" onClick={() => go("calculator")} style={{ fontSize:15, padding:"17px 50px" }}>Begin Free Reading ✦</button>
        </div>
      </section>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CALCULATOR PAGE
───────────────────────────────────────────── */
function Calculator({ fd, setFd, onCalc }) {
  const [step, setStep] = useState(1);
  const [errs, setErrs] = useState({});

  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const year0  = new Date().getFullYear();
  const years  = Array.from({ length:100 }, (_,i) => year0-i);
  const days   = Array.from({ length:31 }, (_,i) => i+1);

  const inp = { width:"100%", padding:"13px 16px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(201,164,101,.22)", borderRadius:10, color:"var(--text)", fontSize:15, fontFamily:"Jost,sans-serif", appearance:"none", WebkitAppearance:"none" };
  const sel = { ...inp, backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7'%3E%3Cpath d='M1 1l5 4.5L11 1' stroke='%23C9A465' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center", cursor:"pointer" };
  const lbl  = { display:"block", fontSize:10.5, color:"rgba(240,235,225,.42)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:7 };

  const v1 = () => { const e={}; if (!fd.day) e.day="Required"; if (!fd.month) e.month="Required"; if (!fd.year) e.year="Required"; setErrs(e); return !Object.keys(e).length; };
  const v2 = () => { const e={}; if (!fd.email||!/\S+@\S+\.\S+/.test(fd.email)) e.email="Please enter a valid email"; setErrs(e); return !Object.keys(e).length; };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"120px 24px 60px", position:"relative", overflow:"hidden" }}>
      <Stars n={55}/>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 25%,rgba(91,63,166,.2) 0%,transparent 58%)", pointerEvents:"none" }}/>
      <div style={{ maxWidth:480, width:"100%", position:"relative", zIndex:1 }}>
        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <svg viewBox="0 0 60 60" width="52" height="52" style={{ marginBottom:18 }}>
            <polygon points="30,3 37,22 57,22 41,35 47,54 30,42 13,54 19,35 3,22 23,22"
              fill="none" stroke="#C9A465" strokeWidth="1.6" opacity=".85"/>
            <circle cx="30" cy="30" r="6" fill="#C9A465" opacity=".75"/>
          </svg>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300, color:"var(--text)", marginBottom:9 }}>
            {step === 1 ? "Your Free Matrix Reading" : "Last Step"}
          </h1>
          <p style={{ color:"var(--text2)", fontSize:14.5, lineHeight:1.65 }}>
            {step === 1 ? "Enter your birth date to generate your sacred Destiny Matrix" : "Where should we send your personalized reading?"}
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ display:"flex", gap:6, marginBottom:26 }}>
          {[1,2].map(s => (
            <div key={s} style={{ flex:1, height:2, borderRadius:1, background: step>=s ? "#C9A465" : "rgba(201,164,101,.13)", transition:"background .35s" }}/>
          ))}
        </div>

        <div className="glass" style={{ padding:"32px 28px" }}>
          {step === 1 ? (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div>
                <label style={lbl}>Your Name <span style={{ opacity:.5 }}>(optional)</span></label>
                <input type="text" placeholder="Enter your name" value={fd.name}
                  onChange={e => setFd(d => ({...d, name:e.target.value}))} style={inp}/>
              </div>
              <div>
                <label style={lbl}>Day of Birth *</label>
                <select value={fd.day} onChange={e => setFd(d => ({...d, day:e.target.value}))}
                  style={{ ...sel, ...(errs.day ? {borderColor:"#ff7070"} : {}) }}>
                  <option value="">Select day</option>
                  {days.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {errs.day && <div style={{ color:"#ff7070", fontSize:11, marginTop:4 }}>Required</div>}
              </div>
              <div>
                <label style={lbl}>Month of Birth *</label>
                <select value={fd.month} onChange={e => setFd(d => ({...d, month:e.target.value}))}
                  style={{ ...sel, ...(errs.month ? {borderColor:"#ff7070"} : {}) }}>
                  <option value="">Select month</option>
                  {months.map((m,i) => <option key={m} value={i+1}>{m}</option>)}
                </select>
                {errs.month && <div style={{ color:"#ff7070", fontSize:11, marginTop:4 }}>Required</div>}
              </div>
              <div>
                <label style={lbl}>Year of Birth *</label>
                <select value={fd.year} onChange={e => setFd(d => ({...d, year:e.target.value}))}
                  style={{ ...sel, ...(errs.year ? {borderColor:"#ff7070"} : {}) }}>
                  <option value="">Select year</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                {errs.year && <div style={{ color:"#ff7070", fontSize:11, marginTop:4 }}>Required</div>}
              </div>
              <button className="gbtn" onClick={() => v1() && setStep(2)} style={{ marginTop:8, width:"100%", padding:15 }}>
                Continue →
              </button>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ padding:15, background:"rgba(201,164,101,.07)", borderRadius:10, border:"1px solid rgba(201,164,101,.2)", textAlign:"center" }}>
                <div style={{ fontSize:9.5, color:"var(--text2)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:5 }}>Reading for</div>
                <div style={{ fontSize:20, fontFamily:"'Cormorant Garamond',serif", color:"var(--text)" }}>
                  {fd.name || "You"} — {fd.day}/{fd.month}/{fd.year}
                </div>
              </div>
              <div>
                <label style={lbl}>Email Address *</label>
                <input type="email" placeholder="your@email.com" value={fd.email}
                  onChange={e => setFd(d => ({...d, email:e.target.value}))}
                  onKeyDown={e => e.key === "Enter" && v2() && onCalc()}
                  style={{ ...inp, ...(errs.email ? {borderColor:"#ff7070"} : {}) }}/>
                {errs.email && <div style={{ color:"#ff7070", fontSize:11, marginTop:4 }}>{errs.email}</div>}
                <div style={{ fontSize:11, color:"rgba(240,235,225,.28)", marginTop:7 }}>🔒 Private & secure — we never share your data</div>
              </div>
              <button className="gbtn" onClick={() => v2() && onCalc()} style={{ marginTop:8, width:"100%", padding:15 }}>
                Reveal My Destiny Matrix ✦
              </button>
              <button onClick={() => setStep(1)} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(240,235,225,.3)", fontSize:13, textDecoration:"underline", fontFamily:"Jost,sans-serif" }}>
                ← Back
              </button>
            </div>
          )}
        </div>
        <div style={{ display:"flex", justifyContent:"center", gap:26, marginTop:22, flexWrap:"wrap" }}>
          {["✦ 100% Free","🔒 Secure & Private","⚡ Instant Results"].map(t => (
            <span key={t} style={{ fontSize:11.5, color:"rgba(240,235,225,.3)" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LOADING SCREEN
───────────────────────────────────────────── */
function Loading({ fd }) {
  const [msg, setMsg] = useState(0);
  const msgs = [
    "Mapping your sacred octagram...",
    "Channeling arcana energies...",
    "Calculating your destiny matrix...",
    "AI is reading your soul blueprint...",
    "Weaving your cosmic portrait...",
  ];
  useEffect(() => {
    const t = setInterval(() => setMsg(m => (m + 1) % msgs.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden", padding:24 }}>
      <Stars n={80}/>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 50%,rgba(91,63,166,.18) 0%,transparent 60%)", pointerEvents:"none" }}/>
      <div style={{ position:"relative", zIndex:1, textAlign:"center" }}>
        <div style={{ position:"relative", width:160, height:160, margin:"0 auto 36px" }}>
          <svg viewBox="0 0 160 160" width="160" height="160" style={{ position:"absolute", inset:0 }}>
            <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(201,164,101,.1)" strokeWidth="1.5"/>
            <circle cx="80" cy="80" r="72" fill="none" stroke="#C9A465" strokeWidth="2"
              strokeDasharray="452" strokeDashoffset="0" strokeLinecap="round"
              style={{ animation:"spin 2.5s linear infinite", transformOrigin:"80px 80px" }}/>
          </svg>
          <svg viewBox="0 0 100 100" width="100" height="100" style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", animation:"spinR 8s linear infinite" }}>
            <polygon points="50,5 63,37 97,37 70,58 80,90 50,70 20,90 30,58 3,37 37,37"
              fill="none" stroke="rgba(201,164,101,.55)" strokeWidth="1.5"/>
          </svg>
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", fontSize:26, color:"#C9A465" }}>✦</div>
        </div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:300, color:"var(--text)", marginBottom:10 }}>
          {fd.name ? `Reading ${fd.name}'s Matrix` : "Reading Your Matrix"}
        </h2>
        <p style={{ color:"var(--gold)", fontSize:14, transition:"opacity .4s", minHeight:22 }}>{msgs[msg]}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   RESULTS PAGE
───────────────────────────────────────────── */
function Results({ mat, interp, fd, go, isPro }) {
  const [tab, setTab] = useState("personality");
  if (!mat) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <p style={{ color:"var(--gold)", marginBottom:16 }}>No matrix found.</p>
        <button className="gbtn" onClick={() => go("calculator")}>Calculate Now</button>
      </div>
    </div>
  );

  const positions = [
    { k:"sky",          l:"Soul Self",    c:"#ADD8E6" },
    { k:"earth",        l:"Life Purpose", c:"#90EE90" },
    { k:"fire",         l:"Destiny",      c:"#FFA500" },
    { k:"water",        l:"Karmic Root",  c:"#87CEEB" },
    { k:"topRight",     l:"Love Energy",  c:"#FF9BB0" },
    { k:"bottomRight",  l:"Abundance",    c:"#FFD700" },
    { k:"bottomLeft",   l:"Health",       c:"#98FB98" },
    { k:"topLeft",      l:"Spirit Gifts", c:"#DDA0DD" },
    { k:"center",       l:"Core Essence", c:"#C9A465" },
  ];

  const tabs = [
    { id:"personality", l:"Personality", ic:"◉", locked:false },
    { id:"love",        l:"Love & Soul", ic:"♡", locked:false },
    { id:"career",      l:"Career",      ic:"◈", locked:true },
    { id:"business",    l:"Business",    ic:"⟡", locked:true },
    { id:"purpose",     l:"Life Purpose",ic:"✦", locked:true },
    { id:"karma",       l:"Karma",       ic:"⟡", locked:true },
  ];

  const gold = "linear-gradient(135deg,#C9A465,#E8C97A)";

  return (
    <div style={{ padding:"100px 24px 60px", maxWidth:1140, margin:"0 auto" }}>
      {/* ── HEADER ── */}
      <div style={{ textAlign:"center", marginBottom:40 }}>
        <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:11 }}>Destiny Matrix Reading</p>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,54px)", fontWeight:300, color:"var(--text)", marginBottom:10 }}>
          {fd.name ? `${fd.name}'s` : "Your"}{" "}
          <span style={{ background:gold, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
            Sacred Matrix
          </span>
        </h1>
        <p style={{ color:"var(--text2)", fontSize:13.5 }}>
          Born {fd.day}/{fd.month}/{fd.year} · Core Essence: {mat.center} — {ARCANA[mat.center]?.name}
        </p>
      </div>

      {/* ── CHART + POSITIONS ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,350px),1fr))", gap:24, marginBottom:24 }}>
        <div className="glass" style={{ padding:26, display:"flex", flexDirection:"column", alignItems:"center" }}>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:400, color:"var(--text)", marginBottom:20, alignSelf:"flex-start" }}>Your Octagram</h2>
          <div style={{ width:"100%", maxWidth:400, paddingBottom:50 }}>
            <Chart mat={mat}/>
          </div>
          <p style={{ fontSize:10, color:"rgba(240,235,225,.26)", letterSpacing:"1.5px" }}>HOVER NODES TO EXPLORE</p>
        </div>

        <div className="glass" style={{ padding:26 }}>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:400, color:"var(--text)", marginBottom:18 }}>Matrix Positions</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {positions.map(({ k, l, c }) => {
              const num = mat[k]; const arc = ARCANA[num];
              return (
                <div key={k} style={{ display:"flex", alignItems:"center", gap:11, padding:"8px 12px", background:"rgba(255,255,255,.025)", borderRadius:9, border:"1px solid rgba(255,255,255,.05)" }}>
                  <div style={{ width:32, height:32, borderRadius:7, background:`${c}18`, border:`1px solid ${c}38`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ fontSize:14, fontFamily:"'Cormorant Garamond',serif", fontWeight:600, color:c }}>{num}</span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:8.5, color:"rgba(240,235,225,.33)", letterSpacing:"1.5px", textTransform:"uppercase" }}>{l}</div>
                    <div style={{ fontSize:13, color:"var(--text)", fontWeight:500, marginTop:1 }}>{arc?.name}</div>
                    <div style={{ fontSize:10.5, color:"rgba(240,235,225,.38)" }}>{arc?.theme}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── AI SUMMARY ── */}
      {interp && (
        <div className="glass" style={{ padding:26, marginBottom:22, borderColor:"rgba(201,164,101,.3)", background:"rgba(201,164,101,.035)" }}>
          <div style={{ display:"flex", gap:13, alignItems:"flex-start" }}>
            <span style={{ color:"var(--gold)", fontSize:20, flexShrink:0, marginTop:2 }}>✦</span>
            <div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:500, color:"var(--text)", marginBottom:9 }}>Soul Blueprint Overview</h3>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:300, color:"rgba(240,235,225,.8)", lineHeight:1.78, fontStyle:"italic" }}>
                {interp.summary}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── INTERPRETATION TABS ── */}
      <div className="glass" style={{ marginBottom:22 }}>
        <div style={{ display:"flex", borderBottom:"1px solid rgba(201,164,101,.1)", overflowX:"auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display:"flex", alignItems:"center", gap:7, padding:"15px 18px",
              background:"none", border:"none", cursor:"pointer", whiteSpace:"nowrap",
              borderBottom: tab===t.id ? "2px solid #C9A465" : "2px solid transparent",
              color: tab===t.id ? "#C9A465" : "rgba(240,235,225,.4)",
              fontFamily:"Jost,sans-serif", fontSize:12, fontWeight:500,
              letterSpacing:".06em", textTransform:"uppercase", transition:"color .2s",
            }}>
              <span>{t.ic}</span><span>{t.l}</span>
              {t.locked && <span style={{ fontSize:9.5, background:"rgba(201,164,101,.16)", color:"var(--gold)", padding:"2px 6px", borderRadius:4 }}>PRO</span>}
            </button>
          ))}
        </div>

        <div style={{ padding:28 }}>
            {interp && tab === "personality" && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,270px),1fr))", gap:26 }}>
                <div>
                  <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:25, fontWeight:400, color:"var(--text)", marginBottom:14 }}>Personality Profile</h3>
                  <p style={{ color:"rgba(240,235,225,.7)", lineHeight:1.85, fontSize:14.5, marginBottom:22 }}>{interp.personality}</p>
                  <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:400, color:"var(--text)", marginBottom:11 }}>Natural Strengths</h4>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                    {(interp.strengths || []).map(s => (
                      <span key={s} style={{ padding:"4px 12px", background:"rgba(201,164,101,.1)", border:"1px solid rgba(201,164,101,.22)", borderRadius:100, fontSize:11.5, color:"var(--gold)", letterSpacing:".5px" }}>{s}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ padding:20, background:"rgba(201,164,101,.05)", borderRadius:11, border:"1px solid rgba(201,164,101,.13)", marginBottom:16 }}>
                    <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, color:"var(--gold)", marginBottom:11 }}>Growth Areas</h4>
                    {(interp.challenges || []).map((c, i) => (
                      <div key={i} style={{ display:"flex", gap:10, marginBottom:10 }}>
                        <span style={{ color:"var(--gold)", flexShrink:0 }}>◈</span>
                        <span style={{ fontSize:13.5, color:"rgba(240,235,225,.58)", lineHeight:1.58 }}>{c}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:20, background:"rgba(91,63,166,.1)", borderRadius:11, border:"1px solid rgba(91,63,166,.24)" }}>
                    <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, color:"#B8A0E8", marginBottom:9 }}>Personal Affirmation</h4>
                    <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, color:"rgba(240,235,225,.78)", lineHeight:1.68, fontStyle:"italic" }}>
                      "{interp.affirmation}"
                    </p>
                  </div>
                </div>
              </div>
            )}
            {interp && tab === "love" && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,270px),1fr))", gap:26 }}>
                <div>
                  <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:25, fontWeight:400, color:"var(--text)", marginBottom:14 }}>Love & Relationship Blueprint</h3>
                  <p style={{ color:"rgba(240,235,225,.7)", lineHeight:1.85, fontSize:14.5, marginBottom:20 }}>{interp.love}</p>
                  <div style={{ padding:18, background:"rgba(255,105,180,.06)", borderRadius:11, border:"1px solid rgba(255,105,180,.14)" }}>
                    <div style={{ fontSize:9, color:"rgba(255,155,176,.65)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:6 }}>Love Arcana</div>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:500, color:"var(--text)" }}>{mat.topRight}</div>
                    <div style={{ fontSize:13.5, color:"rgba(240,235,225,.52)" }}>{ARCANA[mat.topRight]?.name} · {ARCANA[mat.topRight]?.theme}</div>
                  </div>
                </div>
                <div>
                  <div style={{ padding:20, background:"rgba(255,255,255,.03)", borderRadius:11, border:"1px solid rgba(255,255,255,.06)", marginBottom:14 }}>
                    <h4 style={{ fontSize:10, color:"rgba(240,235,225,.38)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:12 }}>Love Traits</h4>
                    {(ARCANA[mat.topRight]?.traits || []).map(t => (
                      <div key={t} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:9 }}>
                        <span style={{ color:"#FF9BB0", fontSize:11 }}>♡</span>
                        <span style={{ fontSize:13.5, color:"rgba(240,235,225,.6)" }}>{t}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:16, background:"rgba(201,164,101,.04)", borderRadius:11, border:"1px solid rgba(201,164,101,.11)" }}>
                    <p style={{ fontSize:13, color:"rgba(240,235,225,.48)", lineHeight:1.68 }}>
                      Your Karmic number {mat.water} ({ARCANA[mat.water]?.name}) shapes ancestral love patterns you're here to transform and transcend in this lifetime.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {tab === "career" && (
              <LockedTab icon="◈" title="Career Matrix" desc="Your professional destiny arcana, career path alignment, and financial blueprint — complete career and wealth positions revealed." go={go} isPro={isPro}>
                {interp?.career && (
                  <p style={{ color:"rgba(240,235,225,.72)", lineHeight:1.85, fontSize:14.5, marginBottom:22 }}>{interp.career}</p>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,240px),1fr))", gap:22 }}>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Career Destiny Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(255,165,0,.05)", border:"1px solid rgba(255,165,0,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#FFA500", minWidth:40, textAlign:"center" }}>{mat.fire}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.fire]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.fire]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.fire]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.fire]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(255,165,0,.08)", border:"1px solid rgba(255,165,0,.18)", borderRadius:100, fontSize:11, color:"#FFA500" }}>{t}</span>)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Financial Blueprint</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(255,215,0,.05)", border:"1px solid rgba(255,215,0,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#FFD700", minWidth:40, textAlign:"center" }}>{mat.bottomRight}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.bottomRight]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.bottomRight]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.bottomRight]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.bottomRight]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(255,215,0,.08)", border:"1px solid rgba(255,215,0,.18)", borderRadius:100, fontSize:11, color:"#FFD700" }}>{t}</span>)}</div>
                  </div>
                </div>
              </LockedTab>
            )}
            {tab === "business" && (
              <LockedTab icon="⟡" title="Business Matrix" desc="Your entrepreneurial authority, business mission arcana, and professional leadership blueprint for building and scaling your vision." go={go} isPro={isPro}>
                {interp?.business && (
                  <p style={{ color:"rgba(240,235,225,.72)", lineHeight:1.85, fontSize:14.5, marginBottom:22 }}>{interp.business}</p>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,240px),1fr))", gap:22 }}>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Business Mission Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(144,238,144,.05)", border:"1px solid rgba(144,238,144,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#90EE90", minWidth:40, textAlign:"center" }}>{mat.earth}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.earth]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.earth]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.earth]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.earth]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(144,238,144,.08)", border:"1px solid rgba(144,238,144,.18)", borderRadius:100, fontSize:11, color:"#90EE90" }}>{t}</span>)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Leadership Authority</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(201,164,101,.07)", border:"1px solid rgba(201,164,101,.18)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"var(--gold)", minWidth:40, textAlign:"center" }}>{mat.center}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.center]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.center]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.center]?.desc}</p>
                    <div style={{ padding:14, background:"rgba(201,164,101,.04)", borderRadius:9, border:"1px solid rgba(201,164,101,.1)", fontSize:13, color:"rgba(240,235,225,.5)", lineHeight:1.7 }}>
                      Your core essence {mat.center} — {ARCANA[mat.center]?.name} — defines how you lead, attract clients, and build lasting professional authority.
                    </div>
                  </div>
                </div>
              </LockedTab>
            )}
            {tab === "purpose" && (
              <LockedTab icon="✦" title="Life Purpose Matrix" desc="Your soul mission, sacred life calling, and purpose-driven path based on your earth and soul arcana." go={go} isPro={isPro}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,240px),1fr))", gap:22 }}>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Life Purpose Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(144,238,144,.05)", border:"1px solid rgba(144,238,144,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#90EE90", minWidth:40, textAlign:"center" }}>{mat.earth}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.earth]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.earth]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.earth]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.earth]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(144,238,144,.08)", border:"1px solid rgba(144,238,144,.18)", borderRadius:100, fontSize:11, color:"#90EE90" }}>{t}</span>)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Soul Self Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(173,216,230,.05)", border:"1px solid rgba(173,216,230,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#ADD8E6", minWidth:40, textAlign:"center" }}>{mat.sky}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.sky]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.sky]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.sky]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.sky]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(173,216,230,.08)", border:"1px solid rgba(173,216,230,.18)", borderRadius:100, fontSize:11, color:"#ADD8E6" }}>{t}</span>)}</div>
                  </div>
                </div>
              </LockedTab>
            )}
            {tab === "karma" && (
              <LockedTab icon="⟡" title="Karmic Blueprint" desc="Past-life karmic patterns, ancestral shadow work, and spirit gifts to develop in this lifetime." go={go} isPro={isPro}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,240px),1fr))", gap:22 }}>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Karmic Root Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(135,206,235,.05)", border:"1px solid rgba(135,206,235,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#87CEEB", minWidth:40, textAlign:"center" }}>{mat.water}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.water]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.water]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:10 }}>{ARCANA[mat.water]?.desc}</p>
                    <div style={{ fontSize:9, color:"rgba(200,150,150,.7)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:7, marginTop:10 }}>Shadow Patterns to Release</div>
                    {(ARCANA[mat.water]?.shadow||[]).map(s => (
                      <div key={s} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                        <span style={{ color:"rgba(200,100,100,.7)", flexShrink:0 }}>◈</span>
                        <span style={{ fontSize:13, color:"rgba(240,235,225,.5)" }}>{s}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:8 }}>Spirit Gifts Arcana</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:"rgba(221,160,221,.05)", border:"1px solid rgba(221,160,221,.12)", borderRadius:10, marginBottom:13 }}>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, color:"#DDA0DD", minWidth:40, textAlign:"center" }}>{mat.topLeft}</div>
                      <div><div style={{ fontSize:13, color:"var(--text)", fontWeight:500 }}>{ARCANA[mat.topLeft]?.name}</div><div style={{ fontSize:11, color:"rgba(240,235,225,.4)" }}>{ARCANA[mat.topLeft]?.theme}</div></div>
                    </div>
                    <p style={{ fontSize:13.5, color:"rgba(240,235,225,.6)", lineHeight:1.75, marginBottom:13 }}>{ARCANA[mat.topLeft]?.desc}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{(ARCANA[mat.topLeft]?.traits||[]).map(t => <span key={t} style={{ padding:"3px 10px", background:"rgba(221,160,221,.08)", border:"1px solid rgba(221,160,221,.18)", borderRadius:100, fontSize:11, color:"#DDA0DD" }}>{t}</span>)}</div>
                  </div>
                </div>
              </LockedTab>
            )}
          </div>
      </div>

      {/* ── YEAR FORECAST (partial lock) ── */}
      {interp && (
        <div className="glass" style={{ padding:26, marginBottom:22, position:"relative", overflow:"hidden" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:400, color:"var(--text)" }}>{new Date().getFullYear()} Energy Forecast</h3>
            <span style={{ padding:"3px 11px", background:"rgba(201,164,101,.13)", border:"1px solid rgba(201,164,101,.26)", borderRadius:100, fontSize:9.5, color:"var(--gold)", letterSpacing:"1px" }}>PREVIEW</span>
          </div>
          <p style={{ color:"rgba(240,235,225,.68)", lineHeight:1.85, fontSize:14.5, marginBottom:18 }}>{interp.yearPrediction}</p>
          <div style={{ position:"relative" }}>
            <p style={{ color:"rgba(240,235,225,.68)", lineHeight:1.85, fontSize:14.5, filter:"blur(5px)", userSelect:"none" }}>
              A major karmic window opens mid-year — one your soul has been preparing for across multiple lifetimes. Relationships and financial streams converge powerfully in Q3. A significant manifestation cycle peaks, bringing long-awaited recognition, deeper intimacy, and material alignment you have earnestly sought...
            </p>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10 }}>
              <span style={{ fontSize:22 }}>🔒</span>
              <button className="gbtn" onClick={() => go("pricing")} style={{ fontSize:12, padding:"9px 20px" }}>Unlock Full Forecast</button>
            </div>
          </div>
        </div>
      )}

      <MonthlyForecast mat={mat} fd={fd} />
      {/* ── EMAIL CAPTURE ── */}
      <EmailCapture type="matrix" mat={mat} fd={fd} interp={interp} />

      {/* ── REFERRAL ── */}
      <ReferralCard />

      {/* ── UPSELL CARDS ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))", gap:15, marginBottom:34 }}>
        {[
          { ic:"♡", t:"Compatibility Report", d:"Discover how your matrix aligns with any partner", cta:"Check Compatibility", p:"$9" },
          { ic:"◈", t:"Full PDF Report",       d:"40-page in-depth matrix analysis to download",    cta:"Get PDF Report",     p:"$19" },
          { ic:"✦", t:"Monthly Insights",      d:"AI-guided wisdom aligned to your matrix",          cta:"Subscribe",          p:"$19/mo" },
        ].map((c, i) => (
          <div key={i} className="glass" style={{ padding:20, textAlign:"center", transition:"transform .25s" }}
            onMouseEnter={e => e.currentTarget.style.transform="translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
            <div style={{ fontSize:26, marginBottom:9, color:"var(--gold)" }}>{c.ic}</div>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:500, color:"var(--text)", marginBottom:6 }}>{c.t}</h3>
            <p style={{ fontSize:12.5, color:"var(--text2)", lineHeight:1.6, marginBottom:13 }}>{c.d}</p>
            <button className="obtn" onClick={() => go("pricing")} style={{ width:"100%", padding:"8px 12px", fontSize:11.5 }}>
              {c.cta} — {c.p}
            </button>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
        <button className="obtn" onClick={() => go("calculator")}>← New Calculation</button>
        <button className="gbtn" style={{ fontSize:13, padding:"12px 24px" }}
          onClick={() => downloadResultsCard(mat, fd)}>
          ↓ Download Share Card
        </button>
        <button onClick={() => shareResultsCard(mat, fd, "whatsapp")}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"12px 20px", borderRadius:6, background:"#25D366", border:"none", color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </button>
        <button onClick={() => shareResultsCard(mat, fd, "instagram")}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"12px 20px", borderRadius:6, background:"linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", border:"none", color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          Instagram
        </button>
        <button className="obtn" onClick={() => printReadingPDF(mat, fd, interp)}>
          Save as PDF
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRICING PAGE
───────────────────────────────────────────── */
function Pricing({ go }) {
  const [bill, setBill] = useState("monthly");
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (planKey) => {
    setLoading(planKey);
    try {
      const res = await fetch(`${API_BASE}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planKey, billing: bill }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        go("calculator");
      }
    } catch {
      go("calculator");
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      n:"Explorer", desc:"For first-time seekers",
      p: bill==="monthly" ? "Free" : "Free", pd:"",
      f:["Basic octagram chart","3 core positions revealed","Personality overview","Love number insight","1 reading per month"],
      cta:"Get Free Reading", hi:false, badge:null,
    },
    {
      n:"Seeker", desc:"Committed to your growth",
      p: bill==="monthly" ? "$19" : "$15", pd:"/mo",
      f:["Everything in Free","All 9 positions revealed","Full AI interpretation","Finance & Career insights","Life Purpose analysis","Karma & shadow work","PDF report download","Monthly energy forecast","Unlimited readings"],
      cta:"Start Seeker Plan", hi:true, badge:"Most Popular",
    },
    {
      n:"Oracle", desc:"For deep seekers & practitioners",
      p: bill==="monthly" ? "$49" : "$39", pd:"/mo",
      f:["Everything in Seeker","Compatibility reports","Business matrix analysis","Annual life cycle map","Priority AI insights","API access (beta)","White-label reports","Monthly advisor call"],
      cta:"Ascend to Oracle", hi:false, badge:"Best Value",
    },
  ];
  const one = [
    { n:"Single Report",    p:"$9",  d:"Complete matrix reading — all positions, full AI interpretation + PDF download" },
    { n:"Compatibility",    p:"$14", d:"Two matrices analyzed for love compatibility and soul connection synergy" },
    { n:"Business Matrix",  p:"$19", d:"Career alignment, financial patterns, and professional success arcana analysis" },
  ];
  const faqs = [
    { q:"How accurate is the Matrix of Destiny?", a:"Our calculation is 100% deterministic — the same birth date always produces identical results. Our AI interprets these numbers using the authentic 22-arcana system to generate deeply meaningful, personalized readings." },
    { q:"What is the Matrix of Destiny?", a:"The Matrix of Destiny is a modern numerological system mapping your birth date onto a sacred 8-pointed star using the 22-arcana Tarot framework. Each position reveals a distinct dimension of your soul: personality, destiny, love, karma, and more." },
    { q:"Can I cancel my subscription anytime?", a:"Yes — cancel with one click, anytime, with no penalties or hidden fees. You retain full access until the end of your billing period." },
    { q:"Is my birth data private?", a:"Completely. Your data is encrypted in transit and at rest, never sold, and never shared with third parties. You can request deletion of all your data at any time." },
  ];
  const gold = "linear-gradient(135deg,#C9A465,#E8C97A)";
  return (
    <div style={{ padding:"120px 24px 80px" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>Pricing</p>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(32px,6vw,64px)", fontWeight:300, color:"var(--text)", lineHeight:1.1, marginBottom:14 }}>
            Choose Your{" "}
            <span style={{ background:gold, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Path</span>
          </h1>
          <p style={{ color:"var(--text2)", fontSize:15.5, maxWidth:440, margin:"0 auto 28px" }}>
            Start free. Upgrade when you're ready. All plans include our core matrix engine.
          </p>
          <div style={{ display:"inline-flex", alignItems:"center", gap:3, background:"rgba(255,255,255,.04)", border:"1px solid rgba(201,164,101,.16)", borderRadius:100, padding:4 }}>
            {["monthly","annual"].map(b => (
              <button key={b} onClick={() => setBill(b)} style={{
                padding:"8px 20px", borderRadius:100, border:"none", cursor:"pointer",
                background: bill===b ? "rgba(201,164,101,.9)" : "transparent",
                color: bill===b ? "#05050E" : "rgba(240,235,225,.48)",
                fontFamily:"Jost,sans-serif", fontSize:12.5, fontWeight:600,
                letterSpacing:".5px", transition:"all .2s", textTransform:"capitalize",
              }}>{b === "annual" ? "Annual (Save 20%)" : "Monthly"}</button>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(275px,1fr))", gap:20, marginBottom:56 }}>
          {plans.map((pl, i) => (
            <div key={i} style={{
              position:"relative",
              background: pl.hi ? "rgba(201,164,101,.05)" : "rgba(255,255,255,.025)",
              border:`1px solid ${pl.hi ? "rgba(201,164,101,.36)" : "rgba(255,255,255,.07)"}`,
              borderRadius:16, padding:28,
              ...(pl.hi ? { boxShadow:"0 0 50px rgba(201,164,101,.09)" } : {}),
            }}>
              {pl.badge && (
                <div style={{ position:"absolute", top:-11, left:"50%", transform:"translateX(-50%)", background:"#C9A465", color:"#05050E", padding:"3px 15px", borderRadius:100, fontSize:9.5, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", whiteSpace:"nowrap" }}>
                  {pl.badge}
                </div>
              )}
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, color:"var(--text)", marginBottom:4 }}>{pl.n}</h2>
              <p style={{ fontSize:12, color:"var(--text2)", marginBottom:16 }}>{pl.desc}</p>
              <div style={{ display:"flex", alignItems:"baseline", gap:3, marginBottom:22 }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:44, fontWeight:300, color: pl.hi ? "var(--gold)" : "var(--text)" }}>{pl.p}</span>
                <span style={{ fontSize:12.5, color:"var(--text2)" }}>{pl.pd}</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                {pl.f.map(f => (
                  <div key={f} style={{ display:"flex", alignItems:"center", gap:9 }}>
                    <span style={{ color:"var(--gold)", fontSize:12.5, flexShrink:0 }}>✓</span>
                    <span style={{ fontSize:13.5, color:"rgba(240,235,225,.6)" }}>{f}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => pl.n === "Explorer" ? go("calculator") : handleCheckout(pl.n.toLowerCase())}
                className={pl.hi ? "gbtn" : "obtn"}
                style={{ width:"100%", padding:13, opacity: loading === pl.n.toLowerCase() ? 0.7 : 1 }}
                disabled={!!loading}
              >{loading === pl.n.toLowerCase() ? "Loading…" : pl.cta}</button>
            </div>
          ))}
        </div>

        {/* One-time reports */}
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:300, color:"var(--text)", textAlign:"center", marginBottom:24 }}>
          One-Time{" "}
          <span style={{ background:gold, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Reports</span>
        </h2>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(235px,1fr))", gap:16, marginBottom:60 }}>
          {one.map((o, i) => (
            <div key={i} className="glass" style={{ padding:22 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:9 }}>
                <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:400, color:"var(--text)" }}>{o.n}</h3>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, color:"var(--gold)", fontWeight:500 }}>{o.p}</span>
              </div>
              <p style={{ fontSize:13, color:"var(--text2)", lineHeight:1.65, marginBottom:14 }}>{o.d}</p>
              <button className="obtn" onClick={() => handleCheckout(i === 0 ? "report_single" : i === 1 ? "report_compat" : "oracle")} style={{ padding:"8px 16px", fontSize:11.5 }} disabled={!!loading}>{loading === String(i) ? "Loading…" : "Get Report"}</button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div style={{ maxWidth:680, margin:"0 auto" }}>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:300, color:"var(--text)", textAlign:"center", marginBottom:26 }}>Common Questions</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
            {faqs.map((faq, i) => (
              <div key={i} className="glass" style={{ padding:20 }}>
                <h3 style={{ fontSize:15, fontWeight:500, color:"var(--text)", marginBottom:8 }}>{faq.q}</h3>
                <p style={{ fontSize:13.5, color:"rgba(240,235,225,.5)", lineHeight:1.78 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPATIBILITY INPUT PAGE
───────────────────────────────────────────── */
/* ─────────────────────────────────────────────
   PERSON FORM (must be outside Compatibility to prevent focus loss)
───────────────────────────────────────────── */
const COMP_INP = { width:"100%", padding:"11px 14px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(201,164,101,.22)", borderRadius:9, color:"var(--text)", fontSize:14, fontFamily:"Jost,sans-serif", appearance:"none", WebkitAppearance:"none" };
const COMP_SEL = { ...COMP_INP, backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7'%3E%3Cpath d='M1 1l5 4.5L11 1' stroke='%23C9A465' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center", cursor:"pointer" };
const COMP_LBL = { display:"block", fontSize:9.5, color:"rgba(240,235,225,.42)", letterSpacing:"2px", textTransform:"uppercase", marginBottom:6 };
const COMP_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const COMP_YEARS = Array.from({ length:100 }, (_,i) => new Date().getFullYear()-i);
const COMP_DAYS  = Array.from({ length:31 }, (_,i) => i+1);

function PersonForm({ fd, setFd, prefix, label, color, errs }) {
  return (
    <div className="glass" style={{ padding:"24px 22px", borderColor: color }}>
      <div style={{ fontSize:10, color, letterSpacing:"3px", textTransform:"uppercase", marginBottom:14, fontWeight:600 }}>{label}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        <div>
          <label style={COMP_LBL}>Name <span style={{ opacity:.5 }}>(optional)</span></label>
          <input
            type="text"
            placeholder="Enter name"
            value={fd.name}
            onChange={e => setFd(d => ({...d, name: e.target.value}))}
            style={COMP_INP}
          />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:9 }}>
          <div>
            <label style={COMP_LBL}>Day *</label>
            <select value={fd.day} onChange={e => setFd(d => ({...d, day:e.target.value}))}
              style={{ ...COMP_SEL, ...(errs[`${prefix}day`] ? {borderColor:"#ff7070"} : {}) }}>
              <option value="">Day</option>
              {COMP_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={COMP_LBL}>Month *</label>
            <select value={fd.month} onChange={e => setFd(d => ({...d, month:e.target.value}))}
              style={{ ...COMP_SEL, ...(errs[`${prefix}month`] ? {borderColor:"#ff7070"} : {}) }}>
              <option value="">Month</option>
              {COMP_MONTHS.map((m,i) => <option key={m} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={COMP_LBL}>Year *</label>
            <select value={fd.year} onChange={e => setFd(d => ({...d, year:e.target.value}))}
              style={{ ...COMP_SEL, ...(errs[`${prefix}year`] ? {borderColor:"#ff7070"} : {}) }}>
              <option value="">Year</option>
              {COMP_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        {(errs[`${prefix}day`] || errs[`${prefix}month`] || errs[`${prefix}year`]) && (
          <div style={{ color:"#ff7070", fontSize:11 }}>Please fill in all date fields</div>
        )}
      </div>
    </div>
  );
}

function Compatibility({ onCalc }) {
  const emptyFd = () => ({ name:"", day:"", month:"", year:"" });
  const [fd1, setFd1] = useState(emptyFd());
  const [fd2, setFd2] = useState(emptyFd());
  const [errs, setErrs] = useState({});

  const validate = () => {
    const e = {};
    if (!fd1.day) e.d1day="Required"; if (!fd1.month) e.d1month="Required"; if (!fd1.year) e.d1year="Required";
    if (!fd2.day) e.d2day="Required"; if (!fd2.month) e.d2month="Required"; if (!fd2.year) e.d2year="Required";
    setErrs(e);
    return !Object.keys(e).length;
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"120px 24px 60px", position:"relative", overflow:"hidden" }}>
      <Stars n={55}/>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 25%,rgba(155,63,166,.18) 0%,transparent 58%)", pointerEvents:"none" }}/>
      <div style={{ maxWidth:600, width:"100%", position:"relative", zIndex:1 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>♡</div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300, color:"var(--text)", marginBottom:9 }}>
            Love Compatibility
          </h1>
          <p style={{ color:"var(--text2)", fontSize:14.5, lineHeight:1.65 }}>
            Enter two birth dates to reveal the cosmic compatibility of your souls
          </p>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:22 }}>
          <PersonForm fd={fd1} setFd={setFd1} prefix="d1" label="♡ Person One" color="rgba(255,155,176,.7)" errs={errs}/>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1, height:"1px", background:"rgba(201,164,101,.15)" }}/>
            <span style={{ fontSize:18, color:"var(--gold)", opacity:.6 }}>✦</span>
            <div style={{ flex:1, height:"1px", background:"rgba(201,164,101,.15)" }}/>
          </div>
          <PersonForm fd={fd2} setFd={setFd2} prefix="d2" label="✦ Person Two" color="rgba(201,164,101,.7)" errs={errs}/>
        </div>

        <button className="gbtn" style={{ width:"100%", padding:15, fontSize:14 }}
          onClick={() => validate() && onCalc(fd1, fd2)}>
          Reveal Soul Compatibility ♡
        </button>
        <div style={{ display:"flex", justifyContent:"center", gap:26, marginTop:18, flexWrap:"wrap" }}>
          {["♡ Free Reading","✦ Instant Results","◉ 22-Arcana System"].map(t => (
            <span key={t} style={{ fontSize:11.5, color:"rgba(240,235,225,.28)" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPAT LOADING
───────────────────────────────────────────── */
function CompatLoading({ fd1, fd2 }) {
  const [msg, setMsg] = useState(0);
  const msgs = [
    "Mapping soul connection points...",
    "Comparing karmic matrices...",
    "Calculating love arcana alignment...",
    "AI is reading your bond...",
    "Weaving your compatibility portrait...",
  ];
  useEffect(() => {
    const t = setInterval(() => setMsg(m => (m + 1) % msgs.length), 1800);
    return () => clearInterval(t);
  }, []);
  const n1 = fd1?.name || "Person 1";
  const n2 = fd2?.name || "Person 2";
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden", padding:24 }}>
      <Stars n={80}/>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 50%,rgba(155,63,166,.18) 0%,transparent 60%)", pointerEvents:"none" }}/>
      <div style={{ position:"relative", zIndex:1, textAlign:"center" }}>
        <div style={{ position:"relative", width:160, height:160, margin:"0 auto 36px", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <svg viewBox="0 0 160 160" width="160" height="160" style={{ position:"absolute", inset:0 }}>
            <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(255,155,176,.1)" strokeWidth="1.5"/>
            <circle cx="80" cy="80" r="72" fill="none" stroke="#FF9BB0" strokeWidth="2"
              strokeDasharray="452" strokeDashoffset="0" strokeLinecap="round"
              style={{ animation:"spin 2.5s linear infinite", transformOrigin:"80px 80px" }}/>
          </svg>
          <div style={{ fontSize:36 }}>♡</div>
        </div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:300, color:"var(--text)", marginBottom:10 }}>
          Reading {n1} & {n2}
        </h2>
        <p style={{ color:"#FF9BB0", fontSize:14, minHeight:22 }}>{msgs[msg]}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPAT RESULTS
───────────────────────────────────────────── */
function CompatResults({ mat1, mat2, fd1, fd2, compat, score, go, isPro }) {
  const n1 = fd1?.name || "Person 1";
  const n2 = fd2?.name || "Person 2";
  const gold = "linear-gradient(135deg,#C9A465,#E8C97A)";
  const pink = "rgba(255,155,176,";

  const scoreColor = score >= 75 ? "#7CFC00" : score >= 55 ? "#C9A465" : "#FF9BB0";
  const scoreLabel = score >= 80 ? "Twin Flame" : score >= 65 ? "Soul Mates" : score >= 50 ? "Deep Connection" : "Growth Partnership";

  const dimensions = [
    { label:"Soul Resonance",  k1:"sky",      k2:"sky",      icon:"✦" },
    { label:"Love Alignment",  k1:"topRight", k2:"topRight", icon:"♡" },
    { label:"Life Purpose",    k1:"earth",    k2:"earth",    icon:"◉" },
    { label:"Karmic Bond",     k1:"water",    k2:"water",    icon:"⟡" },
  ];

  const dimScore = (k1, k2) => {
    const diff = Math.abs(mat1[k1] - mat2[k2]);
    return Math.round(Math.max(0, 1 - diff / 11) * 100);
  };

  return (
    <div style={{ padding:"100px 24px 60px", maxWidth:1100, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ textAlign:"center", marginBottom:40 }}>
        <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:11 }}>Soul Compatibility</p>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(26px,5vw,52px)", fontWeight:300, color:"var(--text)", marginBottom:10 }}>
          {n1}{" "}
          <span style={{ background:"linear-gradient(135deg,#FF9BB0,#FFD0DB)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
            ♡
          </span>{" "}
          {n2}
        </h1>
        <p style={{ color:"var(--text2)", fontSize:13.5 }}>
          {fd1.day}/{fd1.month}/{fd1.year} · {fd2.day}/{fd2.month}/{fd2.year}
        </p>
      </div>

      {/* Score + Charts row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,300px),1fr))", gap:20, marginBottom:22 }}>
        {/* Person 1 chart */}
        <div className="glass" style={{ padding:22, display:"flex", flexDirection:"column", alignItems:"center" }}>
          <div style={{ fontSize:10, color:"rgba(255,155,176,.8)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:14 }}>♡ {n1}</div>
          <div style={{ fontSize:11, color:"var(--text2)", marginBottom:4 }}>{fd1.day}/{fd1.month}/{fd1.year}</div>
          <div style={{ fontSize:13, color:"var(--gold)", marginBottom:18 }}>{ARCANA[mat1.center]?.name} Core</div>
          <Chart mat={mat1} compact={true}/>
        </div>

        {/* Compatibility score */}
        <div className="glass" style={{ padding:22, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14 }}>
          <div style={{ position:"relative", width:140, height:140 }}>
            <svg viewBox="0 0 140 140" width="140" height="140" style={{ position:"absolute", inset:0, transform:"rotate(-90deg)" }}>
              <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="10"/>
              <circle cx="70" cy="70" r="60" fill="none" stroke={scoreColor} strokeWidth="10"
                strokeDasharray={`${score * 3.77} 377`} strokeLinecap="round"
                style={{ transition:"stroke-dasharray 1s ease" }}/>
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, fontWeight:300, color:"var(--text)", lineHeight:1 }}>{score}%</div>
              <div style={{ fontSize:8.5, color:"var(--text2)", letterSpacing:"1.5px", textTransform:"uppercase", marginTop:4 }}>Match</div>
            </div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color: scoreColor, marginBottom:4 }}>{scoreLabel}</div>
            <p style={{ fontSize:12, color:"var(--text2)", lineHeight:1.6, maxWidth:200 }}>
              Based on 9 matrix positions weighted by soul-level significance
            </p>
          </div>
          {/* Dimension bars */}
          <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:9, marginTop:4 }}>
            {dimensions.map(d => {
              const s = dimScore(d.k1, d.k2);
              return (
                <div key={d.label}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:11, color:"var(--text2)" }}>{d.icon} {d.label}</span>
                    <span style={{ fontSize:11, color:"var(--gold)" }}>{s}%</span>
                  </div>
                  <div style={{ height:4, background:"rgba(255,255,255,.06)", borderRadius:2 }}>
                    <div style={{ height:"100%", width:`${s}%`, background: gold, borderRadius:2, transition:"width 1s ease" }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Person 2 chart */}
        <div className="glass" style={{ padding:22, display:"flex", flexDirection:"column", alignItems:"center" }}>
          <div style={{ fontSize:10, color:"rgba(201,164,101,.8)", letterSpacing:"2.5px", textTransform:"uppercase", marginBottom:14 }}>✦ {n2}</div>
          <div style={{ fontSize:11, color:"var(--text2)", marginBottom:4 }}>{fd2.day}/{fd2.month}/{fd2.year}</div>
          <div style={{ fontSize:13, color:"var(--gold)", marginBottom:18 }}>{ARCANA[mat2.center]?.name} Core</div>
          <Chart mat={mat2} compact={true}/>
        </div>
      </div>

      {/* AI Summary */}
      {compat && (
        <div className="glass" style={{ padding:26, marginBottom:20, borderColor:"rgba(255,155,176,.25)", background:`${pink}.03)` }}>
          <div style={{ display:"flex", gap:13, alignItems:"flex-start" }}>
            <span style={{ fontSize:22, flexShrink:0, marginTop:2 }}>♡</span>
            <div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:500, color:"var(--text)", marginBottom:9 }}>Soul Bond Overview</h3>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:300, color:"rgba(240,235,225,.8)", lineHeight:1.78, fontStyle:"italic" }}>
                {compat.bond}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Detail Grid */}
      {compat && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,310px),1fr))", gap:20, marginBottom:20 }}>
          <div className="glass" style={{ padding:24 }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:400, color:"var(--text)", marginBottom:12 }}>Love Dynamic</h3>
            <p style={{ color:"rgba(240,235,225,.68)", lineHeight:1.82, fontSize:14 }}>{compat.dynamic}</p>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div className="glass" style={{ padding:20 }}>
              <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:400, color:"var(--text)", marginBottom:10 }}>Relationship Strengths</h4>
              {(compat.strengths || []).map((s, i) => (
                <div key={i} style={{ display:"flex", gap:9, marginBottom:8 }}>
                  <span style={{ color:"#7CFC00", fontSize:11, flexShrink:0, marginTop:2 }}>✓</span>
                  <span style={{ fontSize:13, color:"rgba(240,235,225,.62)", lineHeight:1.55 }}>{s}</span>
                </div>
              ))}
            </div>
            <div className="glass" style={{ padding:20 }}>
              <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:400, color:"var(--text)", marginBottom:10 }}>Growth Areas</h4>
              {(compat.challenges || []).map((c, i) => (
                <div key={i} style={{ display:"flex", gap:9, marginBottom:8 }}>
                  <span style={{ color:"var(--gold)", fontSize:11, flexShrink:0, marginTop:2 }}>◈</span>
                  <span style={{ fontSize:13, color:"rgba(240,235,225,.58)", lineHeight:1.55 }}>{c}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div className="glass" style={{ padding:20, background:`${pink}.04)`, borderColor:`${pink}.2)` }}>
              <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, color:"#FF9BB0", marginBottom:9 }}>Karmic Contract</h4>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, color:"rgba(240,235,225,.72)", lineHeight:1.72, fontStyle:"italic" }}>
                {compat.karmic}
              </p>
            </div>
            <div className="glass" style={{ padding:20, background:"rgba(91,63,166,.1)", borderColor:"rgba(91,63,166,.24)" }}>
              <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, color:"#B8A0E8", marginBottom:9 }}>Cosmic Advice</h4>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, color:"rgba(240,235,225,.78)", lineHeight:1.68, fontStyle:"italic" }}>
                "{compat.advice}"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Matrix position comparison */}
      <div className="glass" style={{ padding:24, marginBottom:22 }}>
        <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:400, color:"var(--text)", marginBottom:16 }}>Position-by-Position Comparison</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,200px),1fr))", gap:10 }}>
          {[
            { k:"sky", l:"Soul" }, { k:"earth", l:"Purpose" }, { k:"fire", l:"Destiny" },
            { k:"water", l:"Karma" }, { k:"topRight", l:"Love" }, { k:"center", l:"Core" },
          ].map(({ k, l }) => {
            const s = dimScore(k, k);
            const c = s >= 75 ? "#7CFC00" : s >= 50 ? "#C9A465" : "#FF9BB0";
            return (
              <div key={k} style={{ padding:"12px 14px", background:"rgba(255,255,255,.025)", borderRadius:9, border:"1px solid rgba(255,255,255,.05)" }}>
                <div style={{ fontSize:8.5, color:"rgba(240,235,225,.35)", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:7 }}>{l}</div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                  <span style={{ fontSize:13, color:"rgba(255,155,176,.8)" }}>{mat1[k]} · {ARCANA[mat1[k]]?.name}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:13, color:"rgba(201,164,101,.8)" }}>{mat2[k]} · {ARCANA[mat2[k]]?.name}</span>
                </div>
                <div style={{ height:3, background:"rgba(255,255,255,.06)", borderRadius:2 }}>
                  <div style={{ height:"100%", width:`${s}%`, background:c, borderRadius:2 }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── EMAIL CAPTURE ── */}
      <EmailCapture type="compat" mat1={mat1} mat2={mat2} fd1={fd1} fd2={fd2} compat={compat} score={score} />

      {/* ── REFERRAL ── */}
      <ReferralCard />

      <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
        <button className="obtn" onClick={() => go("compatibility")}>← New Reading</button>
        <button className="gbtn" style={{ fontSize:13, padding:"12px 24px" }}
          onClick={() => downloadCompatCard(mat1, mat2, fd1, fd2, score)}>
          ↓ Download Share Card
        </button>
        <button onClick={() => shareCompatCard(mat1, mat2, fd1, fd2, score, "whatsapp")}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"12px 20px", borderRadius:6, background:"#25D366", border:"none", color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </button>
        <button onClick={() => shareCompatCard(mat1, mat2, fd1, fd2, score, "instagram")}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"12px 20px", borderRadius:6, background:"linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", border:"none", color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          Instagram
        </button>
        <button className="obtn" onClick={() => printCompatPDF(mat1, mat2, fd1, fd2, compat, score)}>
          Save as PDF
        </button>
        <button className="obtn" onClick={() => go("calculator")}>Get My Full Reading</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ADMIN DASHBOARD
───────────────────────────────────────────── */
function Admin() {
  const [pw,      setPw]     = useState(() => sessionStorage.getItem("amd_admin_pw") || "");
  const [authed,  setAuthed] = useState(false);
  const [stats,   setStats]  = useState(null);
  const [loading, setLoading]= useState(false);
  const [err,     setErr]    = useState("");
  const [tab,     setTab]    = useState("overview");

  const fetchStats = async (password) => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`, { headers: { "x-admin-password": password } });
      if (!res.ok) { setErr("Incorrect password."); setLoading(false); return; }
      const data = await res.json();
      setStats(data); setAuthed(true);
      sessionStorage.setItem("amd_admin_pw", password);
    } catch { setErr("Connection error."); }
    setLoading(false);
  };

  useEffect(() => { if (pw) fetchStats(pw); }, []);

  const handleLogin = (e) => { e.preventDefault(); fetchStats(pw); };
  const signOut = () => { setAuthed(false); setStats(null); sessionStorage.removeItem("amd_admin_pw"); };

  const TH = ({ children }) => (
    <th style={{ textAlign:"left", fontSize:9, letterSpacing:"2px", textTransform:"uppercase", color:"var(--gold)", borderBottom:"1px solid rgba(201,164,101,.2)", paddingBottom:9, paddingRight:24, whiteSpace:"nowrap" }}>{children}</th>
  );
  const TD = ({ children, mono, dim }) => (
    <td style={{ padding:"9px 24px 9px 0", color: dim ? "rgba(240,235,225,.38)" : "var(--text)", fontFamily: mono ? "monospace" : "inherit", fontSize: mono ? 12.5 : 13.5, verticalAlign:"top", borderBottom:"1px solid rgba(201,164,101,.06)" }}>{children}</td>
  );

  if (!authed) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--bg)" }}>
      <div style={{ background:"rgba(201,164,101,.06)", border:"1px solid rgba(201,164,101,.22)", borderRadius:14, padding:"44px 52px", maxWidth:400, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:10, letterSpacing:"3px", textTransform:"uppercase", color:"var(--gold)", marginBottom:14 }}>✦ Restricted Access</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:"var(--text)", fontWeight:300, marginBottom:30 }}>Admin Dashboard</h2>
        <form onSubmit={handleLogin} style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <input type="password" placeholder="Admin password" value={pw} onChange={e => setPw(e.target.value)} autoFocus
            style={{ padding:"12px 16px", borderRadius:8, border:"1px solid rgba(201,164,101,.28)", background:"rgba(0,0,0,.3)", color:"var(--text)", fontFamily:"inherit", fontSize:14, outline:"none" }} />
          {err && <div style={{ color:"#FF9BB0", fontSize:13 }}>{err}</div>}
          <button type="submit" className="gbtn" disabled={loading} style={{ padding:"13px", fontSize:14 }}>
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </form>
        <p style={{ marginTop:20, fontSize:11.5, color:"rgba(240,235,225,.2)" }}>Authorised personnel only</p>
      </div>
    </div>
  );

  const statCards = [
    { label:"Readings",       value: stats?.readings ?? 0,       icon:"◉", desc:"Personal matrices generated" },
    { label:"Compat Reports", value: stats?.compat   ?? 0,       icon:"♡", desc:"Compatibility readings run" },
    { label:"Email Leads",    value: stats?.totalLeads ?? 0,     icon:"✉", desc:"Subscribers collected" },
    { label:"Referrals",      value: stats?.totalReferrals ?? 0, icon:"✦", desc:"Clicks via referral links" },
  ];

  const TABS = ["overview", "leads", "referrers"];

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", padding:"36px 24px" }}>
      <div style={{ maxWidth:960, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:32, flexWrap:"wrap", gap:14 }}>
          <div>
            <div style={{ fontSize:10, letterSpacing:"3px", textTransform:"uppercase", color:"var(--gold)", marginBottom:6 }}>✦ AI Matrix Destiny</div>
            <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, color:"var(--text)", fontWeight:300, margin:0 }}>Admin Dashboard</h1>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => fetchStats(pw)} className="obtn" style={{ fontSize:12.5, padding:"10px 22px" }}>↻ Refresh</button>
            <button onClick={signOut} className="obtn" style={{ fontSize:12.5, padding:"10px 22px" }}>Sign Out</button>
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:16, marginBottom:34 }}>
          {statCards.map(c => (
            <div key={c.label} style={{ background:"rgba(201,164,101,.06)", border:"1px solid rgba(201,164,101,.18)", borderRadius:10, padding:"22px 24px", textAlign:"center" }}>
              <div style={{ fontSize:26, color:"var(--gold)", marginBottom:6 }}>{c.icon}</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:40, color:"var(--gold)", fontWeight:300, lineHeight:1 }}>{c.value.toLocaleString()}</div>
              <div style={{ fontSize:9.5, letterSpacing:"2px", textTransform:"uppercase", color:"var(--text2)", marginTop:7 }}>{c.label}</div>
              <div style={{ fontSize:11.5, color:"rgba(240,235,225,.28)", marginTop:4 }}>{c.desc}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:4, marginBottom:24 }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:"9px 22px", borderRadius:100, border:"1px solid", fontSize:12.5, fontFamily:"inherit", fontWeight:500, letterSpacing:".05em", textTransform:"uppercase", cursor:"pointer", transition:"all .2s",
                background: tab === t ? "linear-gradient(135deg,#C9A465,#E8C97A)" : "transparent",
                color:       tab === t ? "#05050E" : "var(--gold)",
                borderColor: tab === t ? "transparent" : "rgba(201,164,101,.3)" }}>
              {t === "overview" ? "Overview" : t === "leads" ? `Leads (${stats?.totalLeads ?? 0})` : `Top Referrers (${stats?.topReferrers?.length ?? 0})`}
            </button>
          ))}
        </div>

        {/* Overview tab — summary + recent leads preview */}
        {tab === "overview" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            <div style={{ background:"rgba(201,164,101,.04)", border:"1px solid rgba(201,164,101,.14)", borderRadius:10, padding:"22px 26px" }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:"var(--gold)", fontWeight:400, marginBottom:16 }}>Recent Email Leads</h3>
              {stats?.leads?.length ? (
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr><TH>Name</TH><TH>Email</TH><TH>Type</TH></tr></thead>
                  <tbody>
                    {stats.leads.slice(0, 8).map((l, i) => (
                      <tr key={i}><TD>{l.name || "—"}</TD><TD mono>{l.email}</TD><TD dim>{l.type}</TD></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ color:"var(--text2)", fontSize:13 }}>No leads yet.</p>}
            </div>
            <div style={{ background:"rgba(201,164,101,.04)", border:"1px solid rgba(201,164,101,.14)", borderRadius:10, padding:"22px 26px" }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:"var(--gold)", fontWeight:400, marginBottom:16 }}>Top Referrers</h3>
              {stats?.topReferrers?.length ? (
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr><TH>Rank</TH><TH>Code</TH><TH>Referrals</TH></tr></thead>
                  <tbody>
                    {stats.topReferrers.slice(0, 8).map((r, i) => (
                      <tr key={r.code}><TD dim>#{i+1}</TD><TD mono>{r.code}</TD><TD>{r.count}</TD></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ color:"var(--text2)", fontSize:13 }}>No referrals yet.</p>}
            </div>
          </div>
        )}

        {/* Leads tab */}
        {tab === "leads" && (
          <div style={{ background:"rgba(201,164,101,.04)", border:"1px solid rgba(201,164,101,.14)", borderRadius:10, padding:"24px 28px" }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:"var(--gold)", fontWeight:400, marginBottom:18 }}>
              All Email Leads <span style={{ fontSize:14, color:"var(--text2)", fontStyle:"italic" }}>({stats?.totalLeads ?? 0} total)</span>
            </h3>
            {stats?.leads?.length ? (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13.5, minWidth:500 }}>
                  <thead><tr><TH>Name</TH><TH>Email</TH><TH>Type</TH><TH>Date</TH></tr></thead>
                  <tbody>
                    {stats.leads.map((l, i) => (
                      <tr key={i}>
                        <TD>{l.name || "—"}</TD>
                        <TD mono>{l.email}</TD>
                        <TD dim>{l.type || "general"}</TD>
                        <TD dim>{l.savedAt ? new Date(l.savedAt).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }) : "—"}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p style={{ color:"var(--text2)", fontSize:13.5 }}>No email leads recorded yet.</p>}
          </div>
        )}

        {/* Referrers tab */}
        {tab === "referrers" && (
          <div style={{ background:"rgba(201,164,101,.04)", border:"1px solid rgba(201,164,101,.14)", borderRadius:10, padding:"24px 28px" }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:"var(--gold)", fontWeight:400, marginBottom:18 }}>
              Referral Leaderboard <span style={{ fontSize:14, color:"var(--text2)", fontStyle:"italic" }}>({stats?.totalReferrals ?? 0} total clicks)</span>
            </h3>
            {stats?.topReferrers?.length ? (
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13.5 }}>
                <thead><tr><TH>Rank</TH><TH>Code</TH><TH>Referrals</TH><TH>Share URL</TH></tr></thead>
                <tbody>
                  {stats.topReferrers.map((r, i) => (
                    <tr key={r.code}>
                      <TD dim>#{i+1}</TD>
                      <TD mono>{r.code}</TD>
                      <TD><span style={{ color:"var(--gold)", fontWeight:600 }}>{r.count}</span></TD>
                      <TD><span style={{ fontFamily:"monospace", fontSize:11.5, color:"rgba(240,235,225,.35)" }}>/?ref={r.code}</span></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color:"var(--text2)", fontSize:13.5 }}>No referrals yet.</p>}
          </div>
        )}

        <p style={{ marginTop:28, textAlign:"center", fontSize:11.5, color:"rgba(240,235,225,.18)" }}>
          AI Matrix Destiny · Admin Dashboard · Access via /admin
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   REFERRAL UTILITIES
───────────────────────────────────────────── */
const REF_CODE_KEY = "amd_ref_code";
const REF_CHARS    = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function getOrCreateRefCode() {
  let code = localStorage.getItem(REF_CODE_KEY);
  if (!code) {
    code = Array.from({ length: 6 }, () =>
      REF_CHARS[Math.floor(Math.random() * REF_CHARS.length)]
    ).join("");
    localStorage.setItem(REF_CODE_KEY, code);
  }
  return code;
}

async function trackIncomingReferral(myCode) {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (!ref || ref === myCode) return;
    const sessionKey = `amd_tracked_${ref}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");
    await fetch(`${API_BASE}/api/referral/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: ref }),
    });
  } catch {}
}

const REF_MILESTONES = [
  { n:1,  label:"Star Seeker",   icon:"✦" },
  { n:3,  label:"Cosmic Guide",  icon:"◈" },
  { n:5,  label:"Matrix Oracle", icon:"⟡" },
  { n:10, label:"Soul Master",   icon:"♛" },
];

function ReferralCard() {
  const [code]   = useState(() => getOrCreateRefCode());
  const [count,   setCount]  = useState(null);
  const [copied,  setCopied] = useState(false);

  const refUrl = `${window.location.origin}/?ref=${code}`;

  useEffect(() => {
    fetch(`/api/referral/count?code=${code}`)
      .then(r => r.json())
      .then(d => setCount(d.count || 0))
      .catch(() => setCount(0));
  }, [code]);

  const copy = () => {
    navigator.clipboard.writeText(refUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const earned = REF_MILESTONES.filter(m => count !== null && count >= m.n);
  const next    = REF_MILESTONES.find(m => count !== null && count < m.n);
  const badge   = earned[earned.length - 1];

  return (
    <div style={{ background:"rgba(201,164,101,.06)", border:"1px solid rgba(201,164,101,.22)", borderRadius:12, padding:"26px 30px", maxWidth:640, margin:"0 auto 32px", textAlign:"center" }}>
      <div style={{ fontSize:9.5, letterSpacing:"2.5px", textTransform:"uppercase", color:"var(--gold)", marginBottom:10 }}>✦ Refer Friends</div>
      <p style={{ fontSize:13.5, color:"var(--text2)", lineHeight:1.7, marginBottom:20 }}>
        Share your link and grow your cosmic circle.
        {badge && <span style={{ color:"var(--gold)", fontWeight:600 }}> You've earned the <em>{badge.label}</em> {badge.icon} badge!</span>}
      </p>

      <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:22, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200, maxWidth:360, background:"rgba(0,0,0,.28)", border:"1px solid rgba(201,164,101,.18)", borderRadius:6, padding:"9px 14px", fontFamily:"monospace", fontSize:12.5, color:"var(--text)", textAlign:"left", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {refUrl}
        </div>
        <button onClick={copy}
          style={{ padding:"9px 18px", borderRadius:6, background: copied ? "rgba(40,200,100,.2)" : "rgba(201,164,101,.15)", border:`1px solid ${copied ? "rgba(40,200,100,.5)" : "rgba(201,164,101,.35)"}`, color: copied ? "#6FCF97" : "var(--gold)", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer", transition:"all .2s", whiteSpace:"nowrap" }}>
          {copied ? "✓ Copied!" : "Copy Link"}
        </button>
      </div>

      <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap", marginBottom:18 }}>
        {REF_MILESTONES.map(m => {
          const done = count !== null && count >= m.n;
          return (
            <div key={m.n} style={{ textAlign:"center", opacity: done ? 1 : 0.35, transition:"opacity .3s" }}>
              <div style={{ fontSize:24, marginBottom:4, filter: done ? "none" : "grayscale(1)" }}>{m.icon}</div>
              <div style={{ fontSize:8.5, letterSpacing:"1.5px", textTransform:"uppercase", color: done ? "var(--gold)" : "var(--text2)", marginBottom:2 }}>{m.n} friend{m.n > 1 ? "s" : ""}</div>
              <div style={{ fontSize:11, color: done ? "var(--gold)" : "var(--text2)", fontStyle:"italic" }}>{m.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize:12.5, color:"var(--text2)" }}>
        {count === null
          ? "Loading your referral count…"
          : count === 0
            ? "No referrals yet — share your link to get started."
            : `${count} friend${count > 1 ? "s" : ""} referred so far.${next ? ` ${next.n - count} more to earn ${next.label} ${next.icon}.` : " You've reached the highest tier! ♛"}`}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EMAIL CAPTURE UTILITIES
───────────────────────────────────────────── */
const LEADS_KEY = "amd_leads";

function saveEmailLead(email, type, name) {
  try {
    const list = JSON.parse(localStorage.getItem(LEADS_KEY) || "[]");
    if (!list.find(l => l.email === email)) {
      list.unshift({ email, type, name, savedAt: new Date().toISOString() });
      localStorage.setItem(LEADS_KEY, JSON.stringify(list.slice(0, 100)));
    }
  } catch {}
}

function buildReadingEmail(mat, fd, interp) {
  const name = fd?.name || "Seeker";
  const born = `${fd?.day}/${fd?.month}/${fd?.year}`;
  const coreA = ARCANA[mat?.center];
  const trunc = (s, n) => s ? (s.length > n ? s.slice(0, n) + "…" : s) : "";
  const posLines = [
    ["Core Essence", "center"], ["Soul Energy", "sky"], ["Love & Relationships", "topRight"],
    ["Destiny", "fire"], ["Life Purpose", "earth"], ["Karmic Root", "water"],
  ].map(([l, k]) => `${l}: ${mat[k]} — ${ARCANA[mat[k]]?.name || ""}`).join("\n");

  const body = [
    "✦ AI MATRIX DESTINY ✦",
    "Your Sacred Matrix Reading",
    "",
    `Name: ${name}`,
    `Born: ${born}`,
    `Reading Date: ${new Date().toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })}`,
    "",
    "══════════════════════",
    "CORE ESSENCE",
    "══════════════════════",
    `Arcana ${mat?.center}: ${coreA?.name || ""}`,
    `"${coreA?.theme || ""}"`,
    "",
    ...(interp ? [
      "══════════════════════",
      "SOUL BLUEPRINT",
      "══════════════════════",
      trunc(interp.summary, 350),
      "",
      "══════════════════════",
      "PERSONALITY PROFILE",
      "══════════════════════",
      trunc(interp.personality, 280),
      "",
      `Strengths: ${(interp.strengths || []).join(" · ")}`,
      `Growth Areas: ${(interp.challenges || []).join(" · ")}`,
      "",
      `Your Affirmation:`,
      `"${interp.affirmation}"`,
      "",
      "══════════════════════",
      "LOVE & RELATIONSHIPS",
      "══════════════════════",
      trunc(interp.love, 280),
      "",
      "══════════════════════",
      `${new Date().getFullYear()} ENERGY FORECAST`,
      "══════════════════════",
      trunc(interp.yearPrediction, 280),
      "",
    ] : []),
    "══════════════════════",
    "YOUR MATRIX POSITIONS",
    "══════════════════════",
    posLines,
    "",
    "──────────────────────",
    "Discover more: aimatrixdestiny.com",
    "For entertainment & self-reflection purposes only.",
  ].join("\n");

  const subject = `Your AI Matrix Destiny Reading — ${name}`;
  return { subject, body };
}

function buildCompatEmail(mat1, mat2, fd1, fd2, compat, score) {
  const n1 = fd1?.name || "Person 1", n2 = fd2?.name || "Person 2";
  const lbl = score >= 80 ? "Twin Flame" : score >= 65 ? "Soul Mates" : score >= 50 ? "Deep Connection" : "Growth Partnership";
  const trunc = (s, n) => s ? (s.length > n ? s.slice(0, n) + "…" : s) : "";

  const body = [
    "✦ AI MATRIX DESTINY ✦",
    "Love Compatibility Reading",
    "",
    `${n1}  ♡  ${n2}`,
    `${fd1?.day}/${fd1?.month}/${fd1?.year}   ·   ${fd2?.day}/${fd2?.month}/${fd2?.year}`,
    "",
    `Compatibility Score: ${score}% — ${lbl}`,
    "",
    ...(compat ? [
      "══════════════════════",
      "SOUL BOND OVERVIEW",
      "══════════════════════",
      trunc(compat.bond, 350),
      "",
      "══════════════════════",
      "LOVE DYNAMIC",
      "══════════════════════",
      trunc(compat.dynamic, 280),
      "",
      "══════════════════════",
      "RELATIONSHIP STRENGTHS",
      "══════════════════════",
      ...(compat.strengths || []).map(s => `✓ ${s}`),
      "",
      "GROWTH AREAS",
      ...(compat.challenges || []).map(c => `◈ ${c}`),
      "",
      "══════════════════════",
      "KARMIC CONTRACT",
      "══════════════════════",
      trunc(compat.karmic, 280),
      "",
      "══════════════════════",
      "COSMIC ADVICE",
      "══════════════════════",
      `"${compat.advice}"`,
      "",
    ] : []),
    "──────────────────────",
    "Discover more: aimatrixdestiny.com",
    "For entertainment & self-reflection purposes only.",
  ].join("\n");

  const subject = `AI Matrix Destiny — ${n1} & ${n2} Compatibility Reading`;
  return { subject, body };
}

/* ─────────────────────────────────────────────
   EMAIL CAPTURE COMPONENT
───────────────────────────────────────────── */
function EmailCapture({ type, mat, fd, interp, mat1, mat2, fd1, fd2, compat, score }) {
  const [email, setEmail]       = useState("");
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState("");

  const validate = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSend = async () => {
    if (!validate(email)) { setError("Please enter a valid email address."); return; }
    setError("");

    const name = type === "matrix" ? (fd?.name || "Seeker") : `${fd1?.name || "Person 1"} & ${fd2?.name || "Person 2"}`;
    saveEmailLead(email, type, name);

    try {
      await fetch(`${API_BASE}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, type }),
      });
    } catch (_) {}

    const { subject, body } = type === "matrix"
      ? buildReadingEmail(mat, fd, interp)
      : buildCompatEmail(mat1, mat2, fd1, fd2, compat, score);

    const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, "_blank");
    setSent(true);
  };

  if (sent) return (
    <div className="glass" style={{ padding:"24px 28px", marginBottom:22, textAlign:"center", borderColor:"rgba(201,164,101,.35)", background:"rgba(201,164,101,.04)" }}>
      <div style={{ fontSize:28, color:"var(--gold)", marginBottom:10 }}>✦</div>
      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:400, color:"var(--text)", marginBottom:6 }}>
        Your reading is on its way
      </p>
      <p style={{ fontSize:13, color:"var(--text2)", lineHeight:1.65 }}>
        Your mail client has opened with the reading pre-filled. Just hit send to receive it in your inbox.
      </p>
    </div>
  );

  return (
    <div className="glass" style={{ padding:"24px 28px", marginBottom:22, borderColor:"rgba(201,164,101,.22)", background:"rgba(201,164,101,.025)" }}>
      <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:16 }}>
        <span style={{ fontSize:22, color:"var(--gold)", flexShrink:0 }}>✉</span>
        <div>
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:400, color:"var(--text)", marginBottom:3 }}>
            Send your reading to your inbox
          </p>
          <p style={{ fontSize:12.5, color:"var(--text2)", lineHeight:1.6 }}>
            Get the full reading delivered as a beautifully formatted email you can keep forever.
          </p>
        </div>
      </div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        <input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleSend()}
          style={{
            flex:1, minWidth:200, background:"rgba(255,255,255,.04)", border:"1px solid rgba(201,164,101,.28)",
            borderRadius:100, padding:"12px 20px", color:"var(--text)", fontFamily:"Jost,sans-serif",
            fontSize:14, outline:"none", transition:"border-color .2s",
          }}
          onFocus={e => e.target.style.borderColor="#C9A465"}
          onBlur={e => e.target.style.borderColor="rgba(201,164,101,.28)"}
        />
        <button className="gbtn" style={{ padding:"12px 26px", fontSize:13, flexShrink:0 }} onClick={handleSend}>
          Send to Inbox
        </button>
      </div>
      {error && <p style={{ fontSize:12, color:"#FF9BB0", marginTop:8, paddingLeft:4 }}>{error}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HISTORY UTILITIES
───────────────────────────────────────────── */
const HISTORY_KEY = "amd_history";
const MAX_HISTORY = 20;

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); }
  catch { return []; }
}

function saveToHistory(entry) {
  const list = loadHistory();
  const id = `${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
  const next = [{ ...entry, id, savedAt: new Date().toISOString() }, ...list].slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
}

function deleteFromHistory(id) {
  const next = loadHistory().filter(e => e.id !== id);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
}

function clearHistory() { localStorage.removeItem(HISTORY_KEY); }

/* ─────────────────────────────────────────────
   HISTORY PAGE
───────────────────────────────────────────── */
function History({ go, onRestore }) {
  const [entries, setEntries] = useState(() => loadHistory());

  const refresh = () => setEntries(loadHistory());

  const handleDelete = (id) => { deleteFromHistory(id); refresh(); };
  const handleClear  = () => { clearHistory(); refresh(); };

  const fmt = iso => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" }) +
      "  ·  " + d.toLocaleTimeString(undefined, { hour:"2-digit", minute:"2-digit" });
  };

  return (
    <div style={{ minHeight:"100vh", paddingTop:110, paddingBottom:80 }}>
      <div style={{ maxWidth:820, margin:"0 auto", padding:"0 24px" }}>
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <p style={{ fontSize:11, color:"var(--gold)", letterSpacing:"3px", textTransform:"uppercase", marginBottom:14 }}>✦ SAVED READINGS</p>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:300, fontSize:"clamp(36px,6vw,58px)", lineHeight:1.1, marginBottom:16 }}>
            Your Reading History
          </h1>
          <p style={{ fontSize:14, color:"var(--text2)", lineHeight:1.7 }}>
            All your past Matrix and Compatibility readings, saved locally on this device.
          </p>
        </div>

        {entries.length === 0 ? (
          <div className="glass" style={{ textAlign:"center", padding:"64px 32px" }}>
            <div style={{ fontSize:48, marginBottom:20, opacity:.3 }}>✦</div>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--text2)", marginBottom:24 }}>
              No readings saved yet
            </p>
            <button className="gbtn" onClick={() => go("calculator")}>Get Your First Reading</button>
          </div>
        ) : (
          <>
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:20 }}>
              <button onClick={handleClear} style={{
                background:"none", border:"1px solid rgba(255,100,100,.3)", color:"rgba(255,120,120,.6)",
                borderRadius:100, padding:"7px 18px", fontSize:11, letterSpacing:".06em",
                textTransform:"uppercase", cursor:"pointer", fontFamily:"Jost,sans-serif", transition:"all .2s"
              }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(255,100,100,.07)"; e.currentTarget.style.color="rgba(255,150,150,.9)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="rgba(255,120,120,.6)"; }}>
                Clear All
              </button>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {entries.map(entry => {
                const isCompat = entry.type === "compat";
                const title = isCompat
                  ? `${entry.fd1?.name || "Person 1"}  ♡  ${entry.fd2?.name || "Person 2"}`
                  : `${entry.fd?.name || "Anonymous"}'s Sacred Matrix`;
                const sub = isCompat
                  ? `${entry.fd1?.day}/${entry.fd1?.month}/${entry.fd1?.year}  ·  ${entry.fd2?.day}/${entry.fd2?.month}/${entry.fd2?.year}`
                  : `Born ${entry.fd?.day}/${entry.fd?.month}/${entry.fd?.year}`;
                const badge = isCompat
                  ? { label:"COMPATIBILITY", bg:"rgba(255,155,176,.12)", color:"rgba(255,155,176,.9)" }
                  : { label:"MATRIX READING", bg:"rgba(201,164,101,.1)", color:"rgba(201,164,101,.9)" };
                const detail = isCompat
                  ? `${entry.score}% Match · ${entry.score >= 80 ? "Twin Flame" : entry.score >= 65 ? "Soul Mates" : entry.score >= 50 ? "Deep Connection" : "Growth Partnership"}`
                  : `Core: ${entry.mat?.center} · ${ARCANA[entry.mat?.center]?.name}`;

                return (
                  <div key={entry.id} className="glass" style={{
                    display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center",
                    gap:20, padding:"22px 26px", transition:"border-color .2s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor="rgba(201,164,101,.35)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor="rgba(201,164,101,.18)"}>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                        <span style={{ fontSize:9, fontWeight:600, letterSpacing:"2px", padding:"3px 10px", borderRadius:100, background:badge.bg, color:badge.color }}>
                          {badge.label}
                        </span>
                        <span style={{ fontSize:11, color:"var(--text2)" }}>{fmt(entry.savedAt)}</span>
                      </div>
                      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:400, marginBottom:5, color:"var(--text)" }}>
                        {title}
                      </p>
                      <p style={{ fontSize:12.5, color:"var(--text2)" }}>{sub}</p>
                      <p style={{ fontSize:12, color:"var(--gold)", marginTop:6, opacity:.8 }}>{detail}</p>
                    </div>
                    <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                      <button className="gbtn" style={{ fontSize:12, padding:"10px 20px", whiteSpace:"nowrap" }}
                        onClick={() => onRestore(entry)}>
                        View →
                      </button>
                      <button onClick={() => handleDelete(entry.id)} style={{
                        background:"none", border:"1px solid rgba(255,100,100,.25)", color:"rgba(255,120,120,.5)",
                        borderRadius:100, padding:"9px 14px", fontSize:11, cursor:"pointer",
                        fontFamily:"Jost,sans-serif", transition:"all .2s", lineHeight:1,
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background="rgba(255,100,100,.08)"; e.currentTarget.style.color="rgba(255,150,150,.8)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="rgba(255,120,120,.5)"; }}>
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ textAlign:"center", marginTop:48 }}>
          <button className="obtn" onClick={() => go("home")}>← Back to Home</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BLOG ARTICLES DATA
───────────────────────────────────────────── */
const BLOG_ARTICLES = [
  {
    slug:"what-is-matrix-of-destiny",
    title:"What Is the Matrix of Destiny? A Complete Beginner's Guide",
    date:"May 1, 2026", readTime:"8 min", category:"Fundamentals",
    excerpt:"The Matrix of Destiny is a sacred numerological system that maps your birth date onto a mystical 8-pointed star. Discover how it works and what it reveals about your soul.",
    body:[
      { h:"What Is the Matrix of Destiny?", p:"The Matrix of Destiny is a modern spiritual system that combines elements of numerology, Tarot, and sacred geometry to create a personalised map of your soul. Developed in the 21st century, it uses your date of birth to calculate nine key positions on a sacred 8-pointed Octagram. Each position reveals a different dimension of who you are: your personality, life purpose, love patterns, karmic inheritance, and more." },
      { h:"How Does It Work?", p:"Your birth date contains three numbers — day, month, and year. Each is reduced until it falls within 1 to 22, mapping to one of the 22 Tarot Major Arcana. Nine positions are then calculated from combinations of your three birth numbers, each revealing a different facet of your soul's blueprint. The result is a completely unique reading for every birth date." },
      { h:"The 9 Key Positions", p:"Every Destiny Matrix contains nine positions: Soul Self (day of birth), Life Purpose (month), Destiny Path (year), Karmic Root, Love Energy, Abundance, Health, Spirit Gifts, and Core Essence — the sum of all four primary numbers. Each position is ruled by one of the 22 arcana, giving you a rich, layered portrait of your multidimensional self." },
      { h:"Why Is It So Accurate?", p:"Unlike generalised sun-sign astrology, the Matrix of Destiny is entirely unique to your specific birth date. No two people born on different dates will have the same matrix. The nine interlocking positions capture the complexity of a real human being — your gifts alongside your shadows, your strengths alongside your growth areas." },
      { h:"How to Get Your Reading", p:"All you need is your date of birth. Our AI-powered calculator processes your numbers instantly and generates a deep, personalised interpretation of every position. The free reading covers your Soul, Love, and Personality positions. Upgrading unlocks all nine positions along with your Karmic blueprint, Life Purpose analysis, and annual energy forecast." },
    ]
  },
  {
    slug:"matrix-destiny-love-compatibility",
    title:"Matrix of Destiny Love Compatibility: How Soul Numbers Predict Relationships",
    date:"May 3, 2026", readTime:"7 min", category:"Love & Relationships",
    excerpt:"Discover how the Matrix of Destiny reveals deep soul compatibility between two people — and why some connections feel written in the stars.",
    body:[
      { h:"Why Love Compatibility Works Differently Here", p:"Most compatibility tools compare surface-level traits or sun signs. The Matrix of Destiny analyses the energetic architecture of two souls and examines how they interact. By comparing the Love Energy position, Karmic Root, Core Essence, and all nine points of both matrices, it reveals not just how compatible two people are, but why — and what the karmic purpose of their connection might be." },
      { h:"The Love Energy Position", p:"The most important position for romantic compatibility is the Love Energy node, calculated from your Soul number and Destiny number. This reveals how you give and receive love, what you seek in a partner, and what relationship dynamic you naturally create. When two people's Love Energy numbers are harmonically aligned, the connection tends to be deeply nourishing and intuitive." },
      { h:"Karmic Contracts and Past Lives", p:"The Destiny Matrix also reveals karmic contracts — pre-agreed soul agreements to help each other grow. These are identified by analysing the Karmic Root positions of both matrices. A karmic connection does not always mean an easy one. Some of the most transformative relationships carry difficulty — precisely because they teach us what our soul most needs to learn." },
      { h:"Understanding Compatibility Scores", p:"Our compatibility calculator assigns a percentage score across eight key dimensions: emotional attunement, spiritual alignment, communication style, karmic synergy, life purpose overlap, abundance compatibility, growth potential, and long-term stability. A score of 70 percent or above indicates strong natural resonance. Lower scores do not mean a relationship cannot work — they indicate more conscious effort will be required." },
      { h:"How to Use Your Reading", p:"The most valuable use of a compatibility reading is understanding the relationship more clearly — not deciding whether to be with someone. Knowing which dimensions are naturally harmonious helps you lean into those strengths. Knowing where friction exists allows more patience and compassion. Every relationship carries exactly the lessons both souls came here to learn together." },
    ]
  },
  {
    slug:"destiny-matrix-career-success",
    title:"Destiny Matrix and Career: How Your Numbers Reveal Your Professional Path",
    date:"May 5, 2026", readTime:"6 min", category:"Career & Purpose",
    excerpt:"Your Destiny Matrix holds powerful clues about your ideal career path, financial patterns, and the professional environments where you naturally thrive.",
    body:[
      { h:"The Three Career Positions", p:"Three positions in your Destiny Matrix are particularly relevant to career and professional life: your Destiny Path (calculated from your birth year), your Life Purpose (from your birth month), and your Abundance Arcana. Together, they reveal not just what you are good at, but what gives your work meaning — and how money and success flow in and out of your life." },
      { h:"Your Destiny Path: Direction of Growth", p:"The Destiny Path describes the overarching trajectory of your professional life — the kind of contribution you are here to make as you grow and mature. Someone with The Empress (3) as their Destiny Path is naturally called toward creative work, caregiving, or entrepreneurship. Someone with The Chariot (7) is drawn to leadership, competitive fields, and roles requiring determination and strategic thinking." },
      { h:"Your Abundance Arcana: Relationship With Money", p:"The Abundance position is one of the most insightful for understanding financial behaviour. It reveals your instinctive relationship with money — how you generate it, how you spend it, where your financial blind spots lie, and what beliefs about wealth you may have inherited from your family lineage. Understanding this position can unlock significant shifts in your financial life." },
      { h:"Aligning Work With Your Soul", p:"The most sustainable careers align with at least two of the three career positions in your matrix. When your daily work resonates with your Life Purpose arcana, you experience meaning beyond salary. When your professional environment matches your Destiny Path, you rise through effortless expression rather than struggle. Our full reading includes a detailed career alignment analysis based on all three positions." },
    ]
  },
  {
    slug:"how-to-read-your-destiny-matrix",
    title:"How to Read Your Destiny Matrix: A Step-by-Step Interpretation Guide",
    date:"May 6, 2026", readTime:"9 min", category:"Guides",
    excerpt:"Learning to read your own Destiny Matrix is easier than you think. This guide walks you through every position and how to interpret the arcana that appear there.",
    body:[
      { h:"Start With the Core Essence", p:"Begin with the number at the centre of the Octagram — your Core Essence. This is the most fundamental truth of your soul. Read the description of this arcana slowly and notice what resonates. Most people find their Core Essence is both immediately recognisable and slightly uncomfortable — because it points to the truest, most unguarded version of themselves." },
      { h:"Read the Four Primary Nodes Next", p:"Turn your attention to the four primary nodes: Soul Self (top), Life Purpose (bottom), Karmic Root (left), and Destiny Path (right). These describe your fundamental soul blueprint. Read each in the context of the others — they work as a system, not in isolation. Notice where they harmonise and where they seem in tension. That tension is often the most productive growth edge in your life." },
      { h:"Then Explore the Four Diagonal Nodes", p:"The four diagonal nodes — Love Energy, Abundance, Health, and Spirit Gifts — add nuance and texture. These describe how your core energies express in specific areas of life. Reading these diagonals helps you understand the full dimensionality of who you are beyond your primary blueprint." },
      { h:"Look for Repeating Numbers", p:"One of the most significant patterns to notice is repeating numbers — the same arcana appearing in two or more positions. Repetition amplifies the energy of that arcana in your life. If Justice (11) appears in both your Karmic Root and Love Energy, themes of fairness and truth will be extremely prominent in your relationships and karmic journey." },
      { h:"Read the Shadow Alongside the Light", p:"Every arcana has a light expression and a shadow expression. The light represents gifts when freely and consciously expressed. The shadow represents what happens when that energy is blocked or distorted. A complete reading includes both dimensions. The shadow is not something to fear — it is the portal through which your greatest growth becomes available." },
    ]
  },
  {
    slug:"matrix-destiny-vs-numerology",
    title:"Matrix of Destiny vs Traditional Numerology: What's the Difference?",
    date:"May 7, 2026", readTime:"5 min", category:"Fundamentals",
    excerpt:"Both systems use your birth date — but the Matrix of Destiny goes far deeper. Here is how they compare.",
    body:[
      { h:"What Traditional Numerology Calculates", p:"Traditional numerology reduces your birth date to a single Life Path Number between 1 and 9 (or master numbers 11, 22, 33). This gives a general description of your personality and life themes. While meaningful, a single number can only capture a fraction of who you are. Two people born on completely different dates can share the same Life Path Number, meaning their readings will be largely identical despite being entirely different people." },
      { h:"What the Matrix of Destiny Does Instead", p:"The Matrix of Destiny calculates nine distinct positions from your birth date, each representing a different dimension of your soul. Rather than reducing everything to a single digit, it uses the full range of 1 to 22 — the 22 Major Arcana — giving a far wider vocabulary for describing human experience. The result is a reading unique to your specific combination of day, month, and year." },
      { h:"The Role of the Tarot Arcana", p:"By incorporating the 22 Tarot archetypes, the Matrix of Destiny draws on thousands of years of accumulated symbolic wisdom. Each arcana carries rich, layered meaning developed across centuries — not just a number, but an entire story, a set of archetypes, a collection of gifts and shadows. This gives the system a depth and poetic richness that purely mathematical numerology cannot match." },
      { h:"Which Should You Use?", p:"Both systems offer genuine insight — they are complementary rather than competitive. Traditional numerology is an excellent entry point. The Matrix of Destiny is the better choice for a comprehensive, nuanced, deeply personalised reading. If you have explored numerology and found it meaningful but limited, the Matrix of Destiny is the natural next step — it will reveal dimensions of yourself that single-number systems simply cannot access." },
    ]
  },
];

/* ─────────────────────────────────────────────
   BLOG LIST PAGE
───────────────────────────────────────────── */
function Blog({ go, setCurrentArticle }) {
  const cats = ["All","Fundamentals","Love & Relationships","Career & Purpose","Guides"];
  const [cat, setCat] = React.useState("All");
  const filtered = cat === "All" ? BLOG_ARTICLES : BLOG_ARTICLES.filter(a => a.category === cat);
  const grd = "linear-gradient(135deg,#C9A465,#E8C97A)";
  return (
    <div style={{ padding:"100px 24px 80px", maxWidth:1100, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:52 }}>
        <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>Knowledge Base</p>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(32px,6vw,62px)", fontWeight:300, color:"var(--text)", lineHeight:1.1, marginBottom:14 }}>
          The Matrix <span style={{ background:grd, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Wisdom</span> Blog
        </h1>
        <p style={{ color:"var(--text2)", fontSize:15, maxWidth:480, margin:"0 auto 28px" }}>
          Deep guides on the Matrix of Destiny — how it works, how to read it, and how to apply it to every area of your life.
        </p>
        <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
          {cats.map(c => (
            <button key={c} onClick={() => setCat(c)} style={{
              padding:"7px 18px", borderRadius:100, border:"1px solid rgba(201,164,101,.28)", cursor:"pointer",
              background: cat===c ? "rgba(201,164,101,.9)" : "transparent",
              color: cat===c ? "#05050E" : "rgba(240,235,225,.5)",
              fontFamily:"Jost,sans-serif", fontSize:12, fontWeight:600, transition:"all .2s",
            }}>{c}</button>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:22 }}>
        {filtered.map(a => (
          <article key={a.slug} className="glass" style={{ padding:28, cursor:"pointer", transition:"transform .25s" }}
            onClick={() => { setCurrentArticle(a); go("article"); }}
            onMouseEnter={e => e.currentTarget.style.transform="translateY(-4px)"}
            onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <span style={{ fontSize:9.5, background:"rgba(201,164,101,.12)", border:"1px solid rgba(201,164,101,.24)", color:"var(--gold)", padding:"3px 10px", borderRadius:100, letterSpacing:"1px" }}>{a.category}</span>
              <span style={{ fontSize:11, color:"rgba(240,235,225,.3)" }}>{a.readTime} read</span>
            </div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:500, color:"var(--text)", lineHeight:1.28, marginBottom:10 }}>{a.title}</h2>
            <p style={{ fontSize:13.5, color:"var(--text2)", lineHeight:1.72, marginBottom:16 }}>{a.excerpt}</p>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:11.5, color:"rgba(240,235,225,.32)" }}>{a.date}</span>
              <span style={{ fontSize:12, color:"var(--gold)", fontWeight:500 }}>Read more →</span>
            </div>
          </article>
        ))}
      </div>
      <div style={{ textAlign:"center", marginTop:52 }}>
        <div className="glass" style={{ display:"inline-block", padding:"28px 44px" }}>
          <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--text)", marginBottom:10 }}>Ready to discover your own matrix?</p>
          <p style={{ fontSize:13.5, color:"var(--text2)", marginBottom:18 }}>Your free reading takes under 60 seconds.</p>
          <button className="gbtn" onClick={() => go("calculator")}>Calculate Free ✦</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BLOG ARTICLE PAGE
───────────────────────────────────────────── */
function Article({ article, go }) {
  if (!article) { go("blog"); return null; }
  return (
    <div style={{ padding:"100px 24px 80px", maxWidth:760, margin:"0 auto" }}>
      <button onClick={() => go("blog")} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(240,235,225,.4)", fontFamily:"Jost,sans-serif", fontSize:13, marginBottom:28, display:"flex", alignItems:"center", gap:6 }}>
        ← Back to Blog
      </button>
      <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:20 }}>
        <span style={{ fontSize:9.5, background:"rgba(201,164,101,.12)", border:"1px solid rgba(201,164,101,.24)", color:"var(--gold)", padding:"3px 10px", borderRadius:100, letterSpacing:"1px" }}>{article.category}</span>
        <span style={{ fontSize:11.5, color:"rgba(240,235,225,.3)" }}>{article.date} · {article.readTime} read</span>
      </div>
      <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,50px)", fontWeight:300, color:"var(--text)", lineHeight:1.18, marginBottom:24 }}>{article.title}</h1>
      <p style={{ fontSize:16, color:"rgba(240,235,225,.7)", lineHeight:1.82, marginBottom:36, fontStyle:"italic", borderLeft:"2px solid rgba(201,164,101,.4)", paddingLeft:18 }}>{article.excerpt}</p>
      <div style={{ display:"flex", flexDirection:"column", gap:28 }}>
        {article.body.map((s, i) => (
          <div key={i}>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, color:"var(--text)", marginBottom:12 }}>{s.h}</h2>
            <p style={{ fontSize:15, color:"rgba(240,235,225,.68)", lineHeight:1.85 }}>{s.p}</p>
          </div>
        ))}
      </div>
      <div className="glass" style={{ padding:28, marginTop:48, textAlign:"center", borderColor:"rgba(201,164,101,.3)" }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--text)", marginBottom:8 }}>Ready to discover your own Destiny Matrix?</p>
        <p style={{ fontSize:13.5, color:"var(--text2)", marginBottom:18 }}>Free reading — instant results — no credit card needed.</p>
        <button className="gbtn" onClick={() => go("calculator")}>Calculate My Matrix Free ✦</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ABOUT PAGE
───────────────────────────────────────────── */
function About({ go }) {
  const grd = "linear-gradient(135deg,#C9A465,#E8C97A)";
  return (
    <div style={{ padding:"100px 24px 80px", maxWidth:800, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:52 }}>
        <p style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"4px", textTransform:"uppercase", marginBottom:13 }}>Our Story</p>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(32px,6vw,60px)", fontWeight:300, color:"var(--text)", marginBottom:14 }}>
          About <span style={{ background:grd, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>AI Matrix Destiny</span>
        </h1>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:22 }}>
        {[
          { h:"Our Mission", p:"AI Matrix Destiny was created with a single purpose: to make the profound wisdom of the Matrix of Destiny accessible to anyone, anywhere in the world. We believe every person deserves to understand the blueprint their soul brought into this lifetime — the gifts, the challenges, the purpose, and the karmic patterns that shape their experience of being alive." },
          { h:"What Is the Matrix of Destiny?", p:"The Matrix of Destiny is a modern spiritual system that combines elements of numerology, the Tarot's 22 Major Arcana, and sacred geometry. Your date of birth is used to calculate nine positions on a sacred 8-pointed star — each revealing a different dimension of your soul. The system has helped millions of people understand themselves and their relationships with unprecedented depth and clarity." },
          { h:"How We Use AI", p:"We have combined the authentic Matrix of Destiny calculation methodology with advanced artificial intelligence to generate readings that are deeply personalised, grammatically precise, and genuinely insightful. Our AI is guided by the symbolic meanings of each of the 22 arcana, producing interpretations that feel human-written because they are built on thousands of years of accumulated archetypal wisdom." },
          { h:"Privacy and Trust", p:"We take your privacy seriously. Your birth date and personal information are used solely to generate your reading. We do not sell your data, do not share it with third parties, and store only what is necessary. You can request deletion of your data at any time by contacting us at privacy@aimatrixdestiny.com." },
          { h:"Disclaimer", p:"AI Matrix Destiny is offered for entertainment and self-reflection purposes. The readings generated are based on the Matrix of Destiny system and are not a substitute for professional psychological, medical, legal, or financial advice. We encourage you to use your reading as a tool for reflection and self-discovery." },
        ].map((s, i) => (
          <div key={i} className="glass" style={{ padding:26 }}>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:400, color:"var(--text)", marginBottom:10 }}>{s.h}</h2>
            <p style={{ fontSize:14.5, color:"rgba(240,235,225,.62)", lineHeight:1.82 }}>{s.p}</p>
          </div>
        ))}
      </div>
      <div style={{ textAlign:"center", marginTop:40 }}>
        <button className="gbtn" onClick={() => go("calculator")}>Get Your Free Reading ✦</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRIVACY POLICY PAGE
───────────────────────────────────────────── */
function Privacy() {
  const sections = [
    { h:"Information We Collect", p:"We collect the information you provide when using our calculator: your name (optional), date of birth, and email address. We also collect standard web analytics data such as page views and session duration." },
    { h:"How We Use Your Information", p:"Your birth date is used exclusively to calculate your Destiny Matrix and generate your AI reading. Your email address is used to send your reading and, if you opt in, periodic updates from AI Matrix Destiny. We never use your information for any other purpose without your explicit consent." },
    { h:"Data Storage and Security", p:"Your data is stored securely using industry-standard encryption. Payment information is processed entirely by Stripe and is never stored on our servers." },
    { h:"Third-Party Services", p:"We use the following third-party services: Anthropic (AI interpretation), Stripe (payment processing), and Brevo (email delivery). We do not sell your data to any third party." },
    { h:"Your Rights", p:"You have the right to access, correct, or delete your personal data at any time. Contact us at privacy@aimatrixdestiny.com. We will respond to all requests within 30 days." },
    { h:"Cookies", p:"We use minimal, essential cookies required for the platform to function. We do not use advertising or tracking cookies." },
    { h:"Contact", p:"For any privacy-related questions, contact us at privacy@aimatrixdestiny.com." },
  ];
  return (
    <div style={{ padding:"100px 24px 80px", maxWidth:760, margin:"0 auto" }}>
      <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,52px)", fontWeight:300, color:"var(--text)", marginBottom:8 }}>Privacy Policy</h1>
      <p style={{ fontSize:13, color:"rgba(240,235,225,.35)", marginBottom:40 }}>Last updated: {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {sections.map((s, i) => (
          <div key={i} style={{ padding:"20px 22px", background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.06)", borderRadius:11 }}>
            <h2 style={{ fontSize:16, fontWeight:500, color:"var(--text)", marginBottom:8 }}>{s.h}</h2>
            <p style={{ fontSize:13.5, color:"rgba(240,235,225,.52)", lineHeight:1.78 }}>{s.p}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TERMS OF SERVICE PAGE
───────────────────────────────────────────── */
function Terms() {
  const sections = [
    { h:"Acceptance of Terms", p:"By accessing or using AI Matrix Destiny, you agree to be bound by these Terms of Service. If you do not agree, please do not use the platform." },
    { h:"Nature of the Service", p:"AI Matrix Destiny provides Destiny Matrix readings for entertainment and self-reflection purposes only. Readings are not predictions of the future, nor a substitute for professional psychological, medical, legal, or financial advice." },
    { h:"Payments and Refunds", p:"Single-purchase reports are non-refundable once generated and delivered. Subscription plans may be cancelled at any time, with access continuing until the end of the current billing period. For technical issues, contact support@aimatrixdestiny.com within 7 days for a full refund." },
    { h:"Intellectual Property", p:"All content on AI Matrix Destiny — including the calculator, readings, blog articles, design, and branding — is protected by copyright. You may not reproduce or distribute our content without explicit written permission." },
    { h:"Limitation of Liability", p:"AI Matrix Destiny shall not be liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount you paid for the specific product giving rise to the claim." },
    { h:"Contact", p:"For questions about these terms, contact us at support@aimatrixdestiny.com." },
  ];
  return (
    <div style={{ padding:"100px 24px 80px", maxWidth:760, margin:"0 auto" }}>
      <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(28px,5vw,52px)", fontWeight:300, color:"var(--text)", marginBottom:8 }}>Terms of Service</h1>
      <p style={{ fontSize:13, color:"rgba(240,235,225,.35)", marginBottom:40 }}>Last updated: {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {sections.map((s, i) => (
          <div key={i} style={{ padding:"20px 22px", background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.06)", borderRadius:11 }}>
            <h2 style={{ fontSize:16, fontWeight:500, color:"var(--text)", marginBottom:8 }}>{s.h}</h2>
            <p style={{ fontSize:13.5, color:"rgba(240,235,225,.52)", lineHeight:1.78 }}>{s.p}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Footer({ go }) {
  return (
    <footer style={{ borderTop:"1px solid rgba(201,164,101,.09)", padding:"52px 24px 26px", background:"rgba(0,0,0,.15)" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:40, marginBottom:40 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:12 }}>
              <svg viewBox="0 0 36 36" width="28" height="28">
                <polygon points="18,3 22,13 33,13 24,20 27,31 18,24 9,31 12,20 3,13 14,13" fill="none" stroke="#C9A465" strokeWidth="1.4"/>
                <circle cx="18" cy="18" r="4" fill="#C9A465"/>
              </svg>
              <span style={{ fontSize:15, fontFamily:"'Cormorant Garamond',serif", color:"var(--text)" }}>AI Matrix Destiny</span>
            </div>
            <p style={{ fontSize:12.5, color:"rgba(240,235,225,.35)", lineHeight:1.75, maxWidth:210 }}>
              The world's most advanced AI-powered Matrix of Destiny calculator. Discover your soul's sacred blueprint.
            </p>
          </div>
          {(() => {
            const NAV = {
              "Calculator":         () => go("calculator"),
              "Compatibility":      () => go("compatibility"),
              "Pricing":            () => go("pricing"),
              "Personality Matrix": () => go("calculator"),
              "Love Compatibility": () => go("compatibility"),
              "Career Matrix":      () => go("calculator"),
              "Business Matrix":    () => go("calculator"),
              "About":              () => go("about"),
              "Privacy Policy":     () => go("privacy"),
              "Terms of Service":   () => go("terms"),
            };
            return [
              ["Product",  ["Calculator","Compatibility","Pricing","API (Beta)"]],
              ["Readings", ["Personality Matrix","Love Compatibility","Career Matrix","Business Matrix"]],
              ["Company",  ["About","Privacy Policy","Terms of Service","Contact"]],
            ].map(([sec, items]) => (
              <div key={sec}>
                <h4 style={{ fontSize:9.5, color:"var(--gold)", letterSpacing:"3px", textTransform:"uppercase", marginBottom:16 }}>{sec}</h4>
                <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                  {items.map(item => (
                    <span key={item}
                      onClick={NAV[item]}
                      style={{ fontSize:13, color:"rgba(240,235,225,.38)", cursor: NAV[item] ? "pointer" : "default", transition:"color .2s" }}
                      onMouseEnter={e => { if (NAV[item]) e.target.style.color="#C9A465"; }}
                      onMouseLeave={e => e.target.style.color="rgba(240,235,225,.38)"}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
        <div style={{ borderTop:"1px solid rgba(201,164,101,.08)", paddingTop:22, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <p style={{ fontSize:12, color:"rgba(240,235,225,.25)" }}>© {new Date().getFullYear()} AI Matrix Destiny Calculator. All rights reserved.</p>
          <p style={{ fontSize:11.5, color:"rgba(240,235,225,.22)", maxWidth:400, textAlign:"right", lineHeight:1.6 }}>
            For entertainment & self-reflection purposes. Readings are not a substitute for professional advice.
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   ROOT APP
───────────────────────────────────────────── */
const PRO_KEY = "amd_pro_unlocked";
function getIsPro() {
  try { return !!localStorage.getItem(PRO_KEY); } catch { return false; }
}
function setProUnlocked() {
  try { localStorage.setItem(PRO_KEY, "1"); } catch {}
}

export default function App() {
  const [page,   setPage]   = useState("home");
  const [fd,     setFd]     = useState({ name:"", day:"", month:"", year:"", email:"" });
  const [mat,    setMat]    = useState(null);
  const [interp, setInterp] = useState(null);
  const [isPro,  setIsPro]  = useState(getIsPro);
  const [payBanner, setPayBanner] = useState(null);

  const [cfd1,   setCfd1]   = useState(null);
  const [cfd2,   setCfd2]   = useState(null);
  const [cmat1,  setCmat1]  = useState(null);
  const [cmat2,  setCmat2]  = useState(null);
  const [compat, setCompat] = useState(null);
  const [cscore, setCscore] = useState(null);
  const [currentArticle, setCurrentArticle] = useState(null);

  const go = p => { setPage(p); window.scrollTo({ top:0, behavior:"smooth" }); };

  useEffect(() => {
    if (window.location.pathname === "/admin") setPage("admin");
    const myCode = getOrCreateRefCode();
    trackIncomingReferral(myCode);

    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      const plan = params.get("plan") || "seeker";
      setProUnlocked();
      setIsPro(true);
      setPayBanner(plan);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setPayBanner(null), 7000);
    }
  }, []);

  useEffect(() => {
    const META = {
      home:             { title:"AI Matrix Destiny — Discover Your Sacred Soul Blueprint",           desc:"Enter your birth date and our AI reveals the sacred blueprint of your soul — personality, purpose, love, karma, and the hidden forces shaping your life." },
      calculator:       { title:"Free Matrix Reading — AI Matrix Destiny Calculator",                desc:"Calculate your free Destiny Matrix in seconds. Enter your birth date for a personalized AI reading of your soul blueprint, love arcana, and life purpose." },
      results:          { title:`${fd.name ? fd.name + "'s" : "Your"} Matrix Reading — AI Matrix Destiny`, desc:"Your AI Destiny Matrix reading — soul blueprint, personality profile, love arcana, karmic patterns, life purpose, and energy forecast." },
      pricing:          { title:"Plans & Pricing — AI Matrix Destiny",                               desc:"Start free or unlock the complete reading — all 9 positions, full AI interpretation, PDF reports, monthly energy forecasts, and more." },
      compatibility:    { title:"Love Compatibility Reading — AI Matrix Destiny",                    desc:"Discover your soul compatibility. Enter two birth dates for a complete AI-powered love matrix with karmic contract and bond analysis." },
      "compat-results": { title:"Soul Compatibility Report — AI Matrix Destiny",                     desc:"Your AI love compatibility report — soul bond overview, love dynamic, karmic contract, relationship strengths, and position-by-position analysis." },
      blog:             { title:"Matrix of Destiny Blog — Guides & Insights — AI Matrix Destiny",           desc:"Deep guides on the Matrix of Destiny — how it works, how to read it, and how to apply it to love, career, and personal growth." },
      article:          { title: currentArticle ? `${currentArticle.title} — AI Matrix Destiny` : "Matrix of Destiny Guide — AI Matrix Destiny", desc: currentArticle ? currentArticle.excerpt : "In-depth guide on the Matrix of Destiny system." },
      about:            { title:"About AI Matrix Destiny — Our Mission & Story",                                     desc:"Learn about AI Matrix Destiny — our mission to make the Matrix of Destiny accessible to everyone through AI-powered readings." },
      privacy:          { title:"Privacy Policy — AI Matrix Destiny",                                               desc:"How AI Matrix Destiny collects, uses, and protects your personal information." },
      terms:            { title:"Terms of Service — AI Matrix Destiny",                                             desc:"The terms and conditions governing your use of the AI Matrix Destiny platform." },
      history:          { title:"Reading History — AI Matrix Destiny",                               desc:"Review all your past Destiny Matrix readings and love compatibility reports saved on this device." },
    };
    const m = META[page];
    if (!m) return;
    document.title = m.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", m.desc);
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", m.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", m.desc);
    document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", m.title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", m.desc);
  }, [page, fd.name]);

  const onCalc = async () => {
    const m = calcMatrix(Number(fd.day), Number(fd.month), Number(fd.year));
    setMat(m);
    setInterp(null);
    go("loading");
    const result = await getInterpretation(m, fd);
    setInterp(result);
    saveToHistory({ type:"matrix", fd, mat:m, interp:result });
    go("results");
  };

  const onCompatCalc = async (fd1, fd2) => {
    const m1 = calcMatrix(Number(fd1.day), Number(fd1.month), Number(fd1.year));
    const m2 = calcMatrix(Number(fd2.day), Number(fd2.month), Number(fd2.year));
    const s  = calcCompatibilityScore(m1, m2);
    setCfd1(fd1); setCfd2(fd2); setCmat1(m1); setCmat2(m2); setCscore(s);
    setCompat(null);
    go("compat-loading");
    const result = await getCompatibilityReading(m1, m2, fd1, fd2, s);
    setCompat(result);
    saveToHistory({ type:"compat", fd1, fd2, mat1:m1, mat2:m2, compat:result, score:s });
    go("compat-results");
  };

  const onRestore = (entry) => {
    if (entry.type === "matrix") {
      setFd(entry.fd);
      setMat(entry.mat);
      setInterp(entry.interp);
      go("results");
    } else {
      setCfd1(entry.fd1); setCfd2(entry.fd2);
      setCmat1(entry.mat1); setCmat2(entry.mat2);
      setCompat(entry.compat); setCscore(entry.score);
      go("compat-results");
    }
  };

  const isLoading = page === "loading" || page === "compat-loading";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Jost:wght@300;400;500;600;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        :root{
          --bg:#05050E;--bg2:#0C0C1E;--bg3:#10102A;
          --gold:#C9A465;--gold2:#E8C97A;--copper:#B87333;
          --text:#F0EBE1;--text2:rgba(240,235,225,0.52);
          --border:rgba(201,164,101,0.18);
        }
        body,html{background:var(--bg);font-family:'Jost',sans-serif;color:var(--text);min-height:100vh}
        .glass{background:rgba(255,255,255,0.033);backdrop-filter:blur(20px);border:1px solid var(--border);border-radius:14px}
        select option{background:#0C0C1E;color:var(--text)}
        input::placeholder{color:rgba(240,235,225,0.28)}
        @keyframes twinkle{0%,100%{opacity:.07}50%{opacity:.85}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes spinR{to{transform:rotate(-360deg)}}
        @keyframes pulse{0%,100%{opacity:.55}50%{opacity:1}}
        .gbtn{background:linear-gradient(135deg,#C9A465,#E8C97A);color:#05050E;font-family:'Jost',sans-serif;font-weight:700;font-size:14px;border:none;border-radius:100px;padding:14px 32px;cursor:pointer;letter-spacing:.06em;text-transform:uppercase;transition:transform .25s,box-shadow .25s}
        .gbtn:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(201,164,101,.42)}
        .obtn{background:transparent;color:var(--gold);font-family:'Jost',sans-serif;font-weight:500;font-size:13.5px;border:1px solid rgba(201,164,101,.33);border-radius:100px;padding:13px 30px;cursor:pointer;letter-spacing:.06em;text-transform:uppercase;transition:all .25s}
        .obtn:hover{background:rgba(201,164,101,.07);border-color:var(--gold);transform:translateY(-2px)}
        input:focus,select:focus{border-color:var(--gold)!important;box-shadow:0 0 0 3px rgba(201,164,101,.1)!important;outline:none}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:var(--bg)}::-webkit-scrollbar-thumb{background:rgba(201,164,101,.35);border-radius:2px}
      `}</style>

      {!isLoading && <Nav page={page} go={go}/>}

      {payBanner && (
        <div style={{
          position:"fixed", top:70, left:"50%", transform:"translateX(-50%)", zIndex:9999,
          background:"linear-gradient(135deg,#1A2A0A,#0A1A05)", border:"1px solid #6DBB3A",
          borderRadius:12, padding:"16px 28px", display:"flex", alignItems:"center", gap:14,
          boxShadow:"0 8px 40px rgba(109,187,58,.3)", maxWidth:480, width:"calc(100% - 48px)",
          animation:"fadeUp .4s ease",
        }}>
          <span style={{ fontSize:24 }}>✦</span>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:"#C5F077", marginBottom:3 }}>
              Payment Successful — Full Reading Unlocked!
            </div>
            <div style={{ fontSize:12.5, color:"rgba(197,240,119,.65)" }}>
              All PRO tabs are now unlocked. Your sacred blueprint awaits.
            </div>
          </div>
          <button onClick={() => setPayBanner(null)} style={{ marginLeft:"auto", background:"none", border:"none", color:"rgba(197,240,119,.5)", fontSize:18, cursor:"pointer", padding:"0 4px", flexShrink:0 }}>✕</button>
        </div>
      )}

      <main>
        {page === "home"           && <Home go={go}/>}
        {page === "calculator"     && <Calculator fd={fd} setFd={setFd} onCalc={onCalc}/>}
        {page === "loading"        && <Loading fd={fd}/>}
        {page === "results"        && <Results mat={mat} interp={interp} fd={fd} go={go} isPro={isPro}/>}
        {page === "pricing"        && <Pricing go={go}/>}
        {page === "compatibility"  && <Compatibility onCalc={onCompatCalc}/>}
        {page === "compat-loading" && <CompatLoading fd1={cfd1} fd2={cfd2}/>}
        {page === "compat-results" && <CompatResults mat1={cmat1} mat2={cmat2} fd1={cfd1} fd2={cfd2} compat={compat} score={cscore} go={go} isPro={isPro}/>}
        {page === "history"        && <History go={go} onRestore={onRestore}/>}
        {page === "admin"          && <Admin />}
        {page === "blog"           && <Blog go={go} setCurrentArticle={setCurrentArticle}/>}
        {page === "article"        && <Article article={currentArticle} go={go}/>}
        {page === "about"          && <About go={go}/>}
        {page === "privacy"        && <Privacy/>}
        {page === "terms"          && <Terms/>}
      </main>

      {!isLoading && <Footer go={go}/>}
    </>
  );
}
